// fi nav registry (FI-1869): single declarative model of every navigation
// destination and its gating, extracted from the legacy navbar tenant_type x
// role branch matrix (navbar.vue:226-395), the shell rails (report_shell,
// admin_shell, tenant_config_shell, map_shell, booking_shell), and the nav
// utils (map_pages, booking_pages, vsm_access, alerts_access). consumed by
// the horizon nav components (FiTopNav / FiSectionNav) and, later, the
// dashboard card grids. classic surfaces are NOT refactored onto this in v1.
//
// deliberate normalizations vs the legacy code (drift fixes, on purpose):
//   - feature flags are read through isFeatureEnabled() (tbn tenants store
//     string-typed flags; legacy template gates used raw truthiness, so a
//     string 'false' could show an item). exceptions: fleet_assistant_enabled
//     and help_center_enabled keep their default-on `!== false` semantics.
//   - the tenant features map is always guarded (legacy gates like
//     user.features[tenant].alerts_enabled threw when the tenant key missed).
//   - every staff gate uses isEffectiveAdmin (role admin AND not muggle
//     view-as). legacy drifted: the settings gear and the intrastate booking
//     report used raw role checks and survived muggle preview.
//   - labels unified per destination (legacy: 'Map' vs 'Fleet Map', 'VSM' vs
//     'Vehicle Status Manager' for the same route).
//   - the maps tree renders whole for field/driver/customer-care roles who
//     legacy-linked only a standalone vehicle_map; /maps was already
//     deep-linkable by them (no router guard), so this is IA, not access.
//   - future map gains `smart_dispatching_enabled` as a third arm so cm
//     dispatch customers keep it when it leaves the legacy dispatch dropdown.
//   - group message uses hasGroupMessageAccess (alerts_access.js), the
//     audience that helper always defined and nothing ever called; the legacy
//     dispatch/manager-only gate hid the row from every admin. neither
//     group_message_sender route is guarded, so this is IA, not access.
//
// faithful oddities preserved on purpose (do not "fix" without a ticket):
//   - hardcoded tenant gates, centralized in TENANTS below (dattco driver
//     bid, jeffersonlines misc reports, mtrwestern hours reports, anderson
//     fa settings tab + intrastate report).
//   - fasttrak vsm goes straight to vehicle_status_dvir_fasttrak with no
//     feature check (navbar.vue:232,241 behavior).
//   - tbn managers keep vsm history even with views_reports_enabled
//     (showVsmHistoryNavForManager drift, navbar.vue:722-724).
//   - tbn 'parent' tenants (id substring match) shadow every role branch
//     except booking-customer down to fleet map + reports (navbar.vue:306).
//   - router lockdowns for booking-customer and dattco drivers
//     (router/index.js:839-857) are mirrored here so we never render a
//     link the guard would bounce.

import { vsmBoardRouteName, hasVsmAccess, hasVsmHistoryAccess } from '@/utils/vsm_access'
import { effectiveUser, computeEffectiveAdmin } from '@/utils/view_as'
import { isFeatureEnabled, isFeatureNotDisabled, hasGroupMessageAccess } from '@/utils/alerts_access'
import { hasErmNavAccess } from '@/utils/erm_access'
import { hasScorecardCoachingPages } from '@/utils/coaching_access'

// hardcoded tenant ids carried over from legacy gates. candidates for real
// feature flags; keep the list short and loud.
export const TENANTS = {
  DATTCO: 'dattco-413f464d',
  JEFFERSON_LINES: 'jeffersonlines-d637fdad',
  MTR_WESTERN: 'mtrwestern-tbn-3db5da5g7',
  ANDERSON: 'anderson-759g468f',
  HALE: 'haletrans-31f47ab3',
  EBMEYER: 'ebmeyer-08d0ce10',
}

// public legal pages on the company site, linked from both the top-bar help
// menu and the rail's help section
const LEGAL_URLS = {
  terms: 'https://www.fleet-integrations.com/terms.html',
  privacy: 'https://www.fleet-integrations.com/privacy.html',
}

// ---------------------------------------------------------------------------
// context
// ---------------------------------------------------------------------------

// everything a gate is allowed to look at. built once per render from the
// vuex profile state; gates stay pure functions of this object.
export function buildNavContext(user, viewAs) {
  const tenant = (user && user.tenant) || ''
  const features = (user && user.features && user.features[tenant]) || {}
  // muggle preview: an admin viewing as non-admin previews AS a customer-admin
  // (the highest-privilege customer), so the rail matches what a real customer
  // sees rather than an admin with privileges merely stripped. See utils/view_as.
  const isEffectiveAdmin = computeEffectiveAdmin(user, viewAs)
  const eff = effectiveUser(user, viewAs)
  return {
    user: eff,
    role: (eff && eff.role) || '',
    tenant,
    tenantType: (user && user.types && user.types[tenant]) || '',
    features,
    viewAs: viewAs || null,
    isEffectiveAdmin,
    // theme picker gate: open to every signed-in user. classic/horizon/midnight
    // were behind a staff check plus a per-user theme_preview grant while the
    // redesign was in preview; now the only requirement is that there is a user
    // to save the preference against. kept as a named context key so the nav
    // templates keep reading one gate.
    canPickTheme: !!user,
    telematicsType: (user && user.telematics_types && user.telematics_types[tenant]) || '',
  }
}

// ---------------------------------------------------------------------------
// branch predicates (the legacy v-if/v-else-if chains as named functions)
// ---------------------------------------------------------------------------

const OPS_FIELD_ROLES = ['dispatch', 'garage', 'wash-bay']

function isOpsFieldRole(ctx) {
  return OPS_FIELD_ROLES.includes(ctx.role)
}

export function isTbnParent(ctx) {
  // legacy substring match (navbar.vue:306, report_shell.vue:155)
  return ctx.tenantType === 'TBN' && String(ctx.tenant).indexOf('parent') !== -1
}

// ACCOUNT COMPATIBILITY vs enablement. Admin demo mode (gatePasses) forces the
// *_enabled purchase flags on so support can preview a product before the
// account buys it -- but it must NOT surface a feature the account technically
// cannot run. Express such a hard prerequisite as a NON-*_enabled clause in the
// item's gate (telematics provider, tenant type, role): adminDemoCtx leaves
// those real, so the demo bypass can never satisfy them. e.g. the DVIR Defects
// page reads its feed from a DVIR-capable ELD provider (Saucon), so it stays
// hidden on every other account, admin or not, even though dvir_defects_page_enabled
// would be forced on. Verified against prod: every tenant with the page enabled
// is telematics_type SAUCON, so this clause is a no-op for real customers and
// only constrains the demo preview.
const DVIR_DEFECTS_TELEMATICS = ['SAUCON']

export function isDvirDefectsCapable(ctx) {
  return DVIR_DEFECTS_TELEMATICS.includes(ctx.telematicsType)
}

// which legacy navbar branch a cm user falls into (order mirrors the
// template's v-if chain, navbar.vue:255-303)
export function cmBranch(ctx) {
  if (isOpsFieldRole(ctx)) return 'ops'
  if (ctx.role === 'booking-customer') return 'booking-customer'
  if (ctx.role === 'driver') return 'driver'
  if (ctx.role === 'customer-care') return 'customer-care'
  if (ctx.role === 'manager') return 'manager'
  return 'default'
}

// tbn chain (navbar.vue:306-391); 'parent' shadows everything but
// booking-customer because it comes first in the template
export function tbnBranch(ctx) {
  if (isTbnParent(ctx) && ctx.role !== 'booking-customer') return 'parent'
  if (ctx.role === 'booking-customer') return 'booking-customer'
  if (isOpsFieldRole(ctx)) return 'ops'
  if (ctx.role === 'driver') return 'driver'
  if (ctx.role === 'manager') return 'manager'
  return 'default'
}

// FI-2133: tenant types that use the CM page set. MOOVS differs from CM only
// in where bookings come from, so every CM nav gate applies to it unchanged.
// mirrors CM_LIKE_TYPES in fleet-int-api/util/default_nav_favorites.py -- the
// two sides gate the same favorites and must agree or a seeded key silently
// stops rendering.
const CM_LIKE_TYPES = ['CM', 'MOOVS']

function branchOf(ctx) {
  // FI-2133: MOOVS is CM with a different booking source -- without this every
  // branch gate returns null and the tenant renders an empty nav.
  if (CM_LIKE_TYPES.includes(ctx.tenantType)) return cmBranch(ctx)
  if (ctx.tenantType === 'TBN') return tbnBranch(ctx)
  return null
}

function inDefaultBranch(ctx) {
  return branchOf(ctx) === 'default'
}

// the manager branch is a narrower NAV, not a narrower audience. A section
// whose audience includes the manager ROLE has to admit the manager BRANCH
// too, or the role can never reach the section -- the coaching gate below
// spells the same rule out inline. Reach for this rather than inDefaultBranch
// whenever managers are meant to see the section.
function inDefaultOrManagerBranch(ctx) {
  return ['default', 'manager'].includes(branchOf(ctx))
}

export function isDattcoDriver(ctx) {
  return ctx.role === 'driver' && ctx.tenant === TENANTS.DATTCO
}

// drivers get the fleet map and nothing else under Maps. The header note above
// records the original call -- that the maps tree renders whole for
// field/driver/customer-care roles because /maps was already deep-linkable --
// and this reverses it for drivers only: future positions, booking assignments
// and the link builder are dispatch tools. Kept as a predicate rather than
// folded into each gate so the audience is stated once. utils/map_pages.js
// carries the same rule for the map shell, the map dashboard and the site map.
// NOTE: dispatch / garage / wash-bay / customer-care legacy-linked a single
// standalone map too, and are NOT covered here.
function mapsFleetOnly(ctx) {
  return ctx.role === 'driver'
}

// ...and a tenant can take even that one map away. maps_driver_fleet_map_enabled
// is on unless explicitly turned off, so every tenant keeps today's behavior until
// someone changes it. utils/map_pages.js carries the same rule for the map shell,
// the map dashboard and the site map (and through the site map, favorites).
function driverFleetMapHidden(ctx) {
  return ctx.role === 'driver' && !isFeatureNotDisabled(ctx.features.maps_driver_fleet_map_enabled)
}

function isBookingCustomer(ctx) {
  return ctx.role === 'booking-customer'
}

function isDriver(ctx) {
  return ctx.role === 'driver'
}

// a logged-in driver on a TBN Driver Manager tenant gets one Driver Manager page
// -- their own scorecard. the roster, comparison, risk matrix and fleet dashboard
// are management surfaces and stay out of the driver branch; tbn_service scopes
// the scorecard endpoints to the caller so the page can only ever load their own
// row (util/driver_self_service.py).
//
// TBN only, deliberately: CM scorecard rows key on samsara driver ids and a
// FleetInt user record carries no samsara link (only tbn_driver_id, written by the
// TBN driver sync), so there is nothing to scope a CM driver's request by. Until a
// CM tenant's drivers can be identified, showing them this page would mean handing
// them the unscoped cm_* endpoints -- so they don't get it.
function isSelfServiceScorecardDriver(ctx) {
  return ctx.role === 'driver'
    && ctx.tenantType === 'TBN'
    && isFeatureEnabled(ctx.features.driver_scorecard_enabled)
}

// roles the router guard locks to a route whitelist (router/index.js:839-857)
function isLockedRole(ctx) {
  return isBookingCustomer(ctx) || isDattcoDriver(ctx)
}

// ---------------------------------------------------------------------------
// reports predicates (ported from report_shell.vue:143-184)
// ---------------------------------------------------------------------------

const NO_REPORTS_ROLES = ['dispatch', 'driver', 'booking-customer', 'customer-care', 'wash-bay']

export function hasReportsAccess(ctx) {
  if (ctx.tenantType === 'CM-FLEET') return true
  if (ctx.tenantType === 'FASTTRAK') return false
  if (NO_REPORTS_ROLES.includes(ctx.role)) return false
  if (ctx.isEffectiveAdmin) return true
  if (ctx.role === 'garage') return ctx.tenantType !== 'TBN'
  return isFeatureEnabled(ctx.features.views_reports_enabled)
}

export function reportsRestrictedRole(ctx) {
  if (ctx.isEffectiveAdmin) return null
  if (ctx.tenantType === 'CM-FLEET') return null
  if (ctx.role === 'garage') return 'garage'
  if (isTbnParent(ctx) && ctx.role !== 'customer-admin') return 'tbn-parent'
  if (ctx.role === 'manager') return 'manager'
  return null
}

const isTbn = ctx => ctx.tenantType === 'TBN'
const isCmFleet = ctx => ctx.tenantType === 'CM-FLEET'
// CM/TBN/FASTTRAK tenants run live bookings; FLEET and CM-FLEET tenants are
// telematics-only, so booking-driven map pages don't apply to them.
const hasBookingPlatform = ctx => ['CM', 'TBN', 'FASTTRAK'].includes(ctx.tenantType)

function showActualsGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'garage') return false
  if (isCmFleet(ctx)) return false
  if (rr === 'manager' || rr === 'tbn-parent') return true
  return isFeatureEnabled(ctx.features.actuals_enabled)
}

function showPlannedGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'manager' || rr === 'tbn-parent') return false
  if (isCmFleet(ctx)) return false
  return true
}

function showOtpGroup(ctx) {
  if (reportsRestrictedRole(ctx)) return false
  return isFeatureEnabled(ctx.features.views_reports_otp_report_enabled) || isCmFleet(ctx)
}

function showMiscGroup(ctx) {
  const rr = reportsRestrictedRole(ctx)
  if (rr === 'garage' || rr === 'manager') return false
  if (isCmFleet(ctx)) return false
  return true
}

// full (unrestricted) reports audience within a visible group
const fullReports = ctx => !reportsRestrictedRole(ctx)

// audiences whose legacy nav linked reports; report_shell itself upsells
// when hasReportsAccess is false, so this is the link gate, not an access
// gate. also carried verbatim by the board rows that moved from
// reports > planned into dispatch > boards, so their audience is unchanged.
function reportsLinkGate(ctx) {
  return isCmFleet(ctx) ||
    (CM_LIKE_TYPES.includes(ctx.tenantType) && ['manager', 'default'].includes(cmBranch(ctx))) ||
    (ctx.tenantType === 'TBN' && ['parent', 'manager', 'default'].includes(tbnBranch(ctx)))
}

// ---------------------------------------------------------------------------
// vsm / alerts destinations (dynamic route names)
// ---------------------------------------------------------------------------

function vsmRoute(ctx) {
  // includes the fasttrak legacy hardcode, no feature check
  // (navbar.vue:232,241); shared with Setup so both agree on the board
  return vsmBoardRouteName(ctx.tenantType, ctx.features)
}

// who may see vsm entries at all (role/branch matrix from the legacy navbar)
function vsmRoleGate(ctx) {
  return ctx.tenantType === 'FASTTRAK' ||
    (CM_LIKE_TYPES.includes(ctx.tenantType) && ['ops', 'manager', 'default'].includes(cmBranch(ctx))) ||
    (ctx.tenantType === 'TBN' && ['ops', 'manager', 'default'].includes(tbnBranch(ctx)))
}

// a secondary vsm mode item shows only when: the role matrix allows vsm, the
// tenant actually has vsm (base flag), the mode's own flag holds, and the
// mode is not already where the primary item routes (no duplicates)
function vsmModeGate(ctx, modeRouteName, modeFlagEnabled) {
  if (!vsmRoleGate(ctx) || ctx.tenantType === 'FASTTRAK') return false
  if (!isFeatureEnabled(ctx.features.vehicle_status_management_enabled)) return false
  if (!modeFlagEnabled) return false
  return vsmRoute(ctx) !== modeRouteName
}

// the read-only standalone boards each VSM mode page lists in its "More Info"
// modal under "VSM Standalone Mode". keyed by the route vsmRoute() resolves
// to, because the url prefix is per-PAGE, not per-tenant: the same Driver
// board is /vsm-v1/driver on the cm status board and /vsm-maint/driver on cm
// maintenance. a tenant therefore sees exactly the set its own default VSM
// page offers.
//
// two deliberate omissions:
//   - boards the modals render conditionally on summary.visible_*_fields
//     (cm status board's Driver/Cleaning/Maintenance, tbn dvir's Cleaning).
//     that flag comes from the VSM summary api, which the nav never loads, so
//     linking them here would show boards the modal itself hides.
//   - the per-base and per-tag variants, which are built from api-loaded
//     option lists and cannot be enumerated statically.
// both remain reachable from the modal, which is also the only surface that
// can build the vsm_standalone_uuid form of these urls (see vsmStandaloneId).
const VSM_STANDALONE_BOARDS = {
  vehicle_status: [
    ['dispatch', 'Dispatch Board', 'Standalone dispatch board ordered by depart time.', id => '/vsm-v1/reload/' + id],
    ['wash', 'Wash Board', 'Standalone wash board ordered by return time.', id => '/vsm-v1/reloadreturn/' + id],
    ['wash_es', 'Wash Board (Spanish)', 'Standalone mobile wash board in Spanish.', id => '/vsm-es/reload/' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming/reload/' + id],
  ],
  vehicle_status_dvir: [
    ['dispatch', 'Dispatch Board', 'Standalone dispatch board ordered by depart time.', id => '/vsm/reload/' + id],
    ['wash', 'Wash Board', 'Standalone wash board ordered by return time.', id => '/vsm/reloadreturn/' + id],
    ['wash_es', 'Wash Board (Spanish)', 'Standalone mobile wash board in Spanish.', id => '/vsm-es/reload/' + id],
    ['vsm2', 'VSM2 Board', 'The redesigned mobile-friendly standalone board.', id => '/vsm2?tenant=' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming/reload/' + id],
  ],
  vehicle_status_maint: [
    ['board', 'Maintenance Board', 'Standalone maintenance board.', id => '/vsm-maint/reload/' + id],
    ['driver', 'Driver Board', 'Standalone driver-facing maintenance board.', id => '/vsm-maint/driver/' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming/reload/' + id],
  ],
  vehicle_status_tbn: [
    ['board', 'Status Board', 'Standalone copy of this status board.', id => '/vsm-tbn/reload/' + id],
    ['wash', 'Wash Board', 'Standalone wash board ordered by return time.', id => '/vsm-tbn/reloadreturn/' + id],
    ['driver', 'Driver Board', 'Standalone driver-facing board.', id => '/vsm-tbn/driver/' + id],
    ['cleaning', 'Cleaning Board', 'Standalone cleaning-facing board.', id => '/vsm-tbn/cleaning/' + id],
    ['maintenance', 'Maintenance Board', 'Standalone maintenance-facing board.', id => '/vsm-tbn/maintenance/' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming-tbn/reload/' + id],
  ],
  vehicle_status_dvir_tbn: [
    ['board', 'Status Board', 'Standalone status board.', id => '/status/reload/' + id],
    ['active', 'Active Board', 'Standalone board of vehicles currently out.', id => '/status/active/' + id],
    ['driver', 'Driver Board', 'Standalone driver-facing board.', id => '/status/driver/' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming-tbn/reload/' + id],
  ],
  vehicle_status_maint_tbn: [
    ['board', 'Maintenance Board', 'Standalone maintenance board.', id => '/vsm-maint-tbn/reload/' + id],
    ['driver', 'Driver Board', 'Standalone driver-facing maintenance board.', id => '/vsm-maint-tbn/driver/' + id],
    ['upcoming', 'Upcoming Board', 'Standalone timeline of upcoming schedules with wash/maint status.', id => '/vsm-upcoming-tbn/reload/' + id],
  ],
  vehicle_status_dvir_fasttrak: [
    ['dispatch', 'Dispatch Board', 'Standalone dispatch board ordered by depart time.', id => '/vsm-fasttrak/reload/' + id],
    ['vsm2', 'VSM2 Board', 'The redesigned mobile-friendly standalone board.', id => '/vsm2?tenant=' + id + '&src=fasttrak'],
  ],
}

// the id these urls carry. the VSM pages use summary.vsm_standalone_uuid when
// the tenant has one and fall back to the tenant id; the nav has no summary,
// so it always emits the tenant-id form. that works -- the standalone views
// take whatever id is in the path and query as the tenant -- but note these
// pages are NOT behind the login redirect (App.vue exempts /vsm* and
// /status*), so a link copied out of the rail exposes the real tenant id
// where the uuid form would not. the modal keeps its copy buttons for the
// uuid form, which is the one to share.
const vsmStandaloneId = ctx => ctx.tenant

// one item per board, gated so only the set belonging to the tenant's own
// default VSM page renders. vsmRoute returns 'vehstatusmgr' for tenants with
// no vsm at all, which matches no key here, so they get nothing.
function vsmStandaloneItems() {
  const out = []
  Object.keys(VSM_STANDALONE_BOARDS).forEach(mode => {
    VSM_STANDALONE_BOARDS[mode].forEach(([key, label, desc, path]) => {
      out.push({
        key: 'vsm_sa_' + mode + '_' + key,
        label,
        desc,
        external: true,
        href: ctx => './#' + path(vsmStandaloneId(ctx)),
        gate: ctx => vsmRoleGate(ctx) && vsmRoute(ctx) === mode,
      })
    })
  })
  return out
}

// legacy showVsmHistoryNav (navbar.vue:717-721) + the tbn manager variant
// (722-724) that skips both exclusions; cm managers never had the item
function showVsmHistory(ctx) {
  if (!hasVsmHistoryAccess(ctx.user)) return false
  const b = branchOf(ctx)
  if (ctx.tenantType === 'TBN' && b === 'manager') return true
  if (b !== 'default') return false
  return ctx.role !== 'admin' && !isFeatureEnabled(ctx.features.views_reports_enabled)
}

// the retired reports > misc declaration of vsm history, verbatim: reports
// link gate + misc group + full reports + the vsm flag. vsm history now has
// ONE home (monitoring) gated on the union of this and showVsmHistory, so
// every user who saw it anywhere still sees it, in the same place as the
// rest of the vsm family.
function showVsmHistoryReportSide(ctx) {
  if (!fullReports(ctx) || !showMiscGroup(ctx)) return false
  if (!isFeatureEnabled(ctx.features.vehicle_status_management_enabled)) return false
  return reportsLinkGate(ctx)
}

// ---------------------------------------------------------------------------
// section data
// ---------------------------------------------------------------------------
// section: { key, label, icon, desc?, surfacing: 'top'|'utility'|'contextual',
//            landingRoute?, landingQuery?, landingOnly?, gate(ctx),
//            groups: [{ key, label?, icon?, gate?, items }] }
// surfacing 'contextual' = a "you are here" row: rendered in the rail ONLY
// while its page is the active route, so pages owned by the top-bar chrome
// (bell, avatar) still show the selected page in the tree without
// permanently re-listing them. (help was contextual through round 12; round
// 13 promoted it to a real utility section above settings.)
// item: { key, label, icon?, desc?, route|route(ctx), query?|query(ctx),
//         href?(ctx), external?, action?, gate?(ctx), placement?: 'topbar',
//         loose? }
// loose = the row owns its whole multi-tab page: it stays active on any
// query of its route (the tree's disambiguator check is skipped). only for
// routes with a single tree row -- pinned-query siblings need the strict
// match to disambiguate.
// route fns return a route NAME; query fns return a query object; href fns
// return a url string (external links); action items carry a $root event
// name instead of a destination (the rail renders them as buttons).
// omitted gate = inherit group/section.
// desc = the hover-tooltip line; copied from the overview/dashboard pages
// (report_dashboard_shell, admin_tools_dashboard, map_pages, booking_pages,
// tenant_config_shell TAB_OVERVIEW_META) so both surfaces say the same thing.
//
// icons (font awesome 4.7 only -- that is the build we ship) follow one rule:
// a SMALL SHARED VOCABULARY, applied consistently, with uniqueness enforced
// only among rows that can appear as siblings. the column is an index, not
// decoration: it should let the eye find "the driver ones" or "the vehicle
// ones" in a ten-row folder without reading a single label. that is why
// Driver Utilization Daily/Weekly/Monthly deliberately SHARE fa-user-o --
// the subject is the useful signal there, and the granularity is already in
// the label. inventing a distinct glyph per row would encode nothing and
// read as noise, which is the failure mode to avoid.
//
// the vocabulary:
//   driver fa-user-o   drivers/board fa-users   vehicle fa-bus
//   time/on-time fa-clock-o    date range fa-calendar-o   history fa-history
//   money fa-usd       payroll fa-money         mileage/route fa-road
//   map fa-map-marker  list fa-list-alt         diagnose fa-stethoscope
//   trend fa-line-chart  breakdown fa-pie-chart   totals fa-bar-chart
//   board fa-columns   grid fa-th               dispatch fa-paper-plane-o
//   alerts fa-bell-o   emergency fa-exclamation-triangle   blocked fa-ban
//   maintenance fa-wrench   fleet health fa-heartbeat   inspection fa-check-square-o
//   sync fa-refresh    compare fa-balance-scale  assistant fa-magic
//   company fa-building-o   file fa-file-text-o   legal fa-gavel / fa-lock

const SECTIONS = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    icon: 'fa-th-large',
    desc: 'Every page available to you, as a launcher grid.',
    surfacing: 'top',
    landingRoute: () => 'dashboard',
    landingOnly: true, // the dashboard page is itself a launcher grid
    // drivers get no dashboard on any tenant type (the router guard blocks the
    // route too): the launcher grid surfaces pages outside a driver's lane.
    gate: ctx =>
      !isDriver(ctx) && (
        ctx.tenantType === 'FASTTRAK' ||
        ctx.tenantType === 'FLEET' ||
        ctx.tenantType === 'CM-FLEET' ||
        ctx.tenantType === 'SOS' ||   // Bus SOS accounts (incl. yard-only) get the launcher dashboard
        inDefaultBranch(ctx)),
    groups: [],
  },

  {
    key: 'maps',
    label: 'Maps',
    icon: 'fa-map-marker',
    desc: 'Live, future, and historical vehicle maps.',
    surfacing: 'top',
    landingRoute: ctx => {
      if (isBookingCustomer(ctx)) return 'booking_customer_fleet_map'
      if (isDattcoDriver(ctx)) return 'vehicle_map' // guard whitelist, router/index.js:851-857
      return 'map_dashboard'
    },
    // cm booking-customers have no map anywhere in legacy nav or guard whitelist.
    // a driver whose tenant hid the fleet map has no Maps pages left at all, and
    // the section carries a landingRoute -- without this it would survive as an
    // empty rail row pointing at a dashboard with no cards.
    gate: ctx => !(isBookingCustomer(ctx) && ctx.tenantType !== 'TBN') && !driverFleetMapHidden(ctx),
    groups: [
      {
        key: 'maps',
        items: [
          { key: 'fleet_map', label: 'Fleet Map', icon: 'fa-map-marker', route: 'map_fleet',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: ctx => !isLockedRole(ctx) && !driverFleetMapHidden(ctx) },
          { key: 'fleet_map_locked', label: 'Fleet Map', icon: 'fa-map-marker', route: 'vehicle_map',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: ctx => isDattcoDriver(ctx) && !driverFleetMapHidden(ctx) },
          { key: 'fleet_map_booking_customer', label: 'Fleet Map', icon: 'fa-map-marker',
            route: 'booking_customer_fleet_map',
            desc: 'Live map of your fleet\'s current vehicle locations and statuses.',
            gate: ctx => isBookingCustomer(ctx) && ctx.tenantType === 'TBN' },
          { key: 'future_map', label: 'Future Map', icon: 'fa-clock-o', route: 'map_future',
            desc: 'Projected vehicle positions and upcoming dispatch coverage.',
            gate: ctx => hasBookingPlatform(ctx) && !isLockedRole(ctx) && !mapsFleetOnly(ctx) &&
              (ctx.isEffectiveAdmin || isTbn(ctx) || isFeatureEnabled(ctx.features.smart_dispatching_enabled)) },
          { key: 'trip_history', label: 'Vehicle Trip History', icon: 'fa-road', route: 'map_trip_history',
            desc: 'Replay a vehicle\'s historical trips, stops, and geofences on the map.',
            gate: ctx => !isLockedRole(ctx) && !mapsFleetOnly(ctx) },
          { key: 'route_history', label: 'Vehicle Route History', icon: 'fa-location-arrow',
            route: 'map_route_history',
            desc: 'Review completed vehicle routes for SAMSARA-equipped vehicles.',
            gate: ctx => !isLockedRole(ctx) && !mapsFleetOnly(ctx) &&
              (ctx.telematicsType === 'SAMSARA' || isTbn(ctx)) },
          // the rail renders href items as a plain <a target="_blank">, so this
          // has to be a real browser URL, not a router path. the router is
          // hash-mode and cloudfront only falls back to index.html at the
          // root, so a bare /booking-track/<tenant> hits the s3 origin and
          // 403s with access-denied. keep the './#' prefix (same as the vsm
          // standalone items above).
          { key: 'booking_map', label: 'Vehicle Booking Map', icon: 'fa-calendar', external: true,
            href: ctx => './#/booking-track/' + ctx.tenant + (isTbn(ctx) ? '?type=TBN' : ''),
            desc: 'Map vehicles against their assigned bookings and routes.',
            gate: ctx => hasBookingPlatform(ctx) && !isLockedRole(ctx) && !mapsFleetOnly(ctx) },
          // rollout-gated per tenant (maps_link_builder_enabled, off by default),
          // staff always see it. not booking-platform-gated - FLEET/CM-FLEET
          // (telematics-only) tenants have vehicles to link just like CM/TBN.
          // the nightly yard snapshot: a map question ("what was parked where"), captured at each
          // tenant's own local midnight, so it lives with the maps rather than the reports
          { key: 'vehicles_by_yard', label: 'Vehicles by Yard', icon: 'fa-th-large', route: 'vehicles_by_yard_tbn',
            desc: 'Where every vehicle was parked, grouped by yard, at a point in time.',
            gate: ctx => isTbn(ctx) && !isLockedRole(ctx) && !mapsFleetOnly(ctx) },
          { key: 'map_link_builder', label: 'Map Link Builder', icon: 'fa-link', route: 'map_link_builder',
            desc: 'Create shareable live-map links for selected vehicles and a time window.',
            gate: ctx => !isLockedRole(ctx) && !mapsFleetOnly(ctx) &&
              (ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.maps_link_builder_enabled)) },
        ],
      },
    ],
  },

  {
    key: 'bookings',
    label: 'Bookings',
    icon: 'fa-calendar-check-o',
    desc: 'Booking actuals, timelines, and processing history.',
    surfacing: 'top',
    landingRoute: ctx => (isBookingCustomer(ctx) ? 'booking_customer_list_tbn' : 'bookings_dashboard'),
    gate: ctx =>
      isBookingCustomer(ctx) ||
      (ctx.tenantType === 'FASTTRAK' && !isOpsFieldRole(ctx)) ||
      (CM_LIKE_TYPES.includes(ctx.tenantType) && cmBranch(ctx) === 'default') ||
      (ctx.tenantType === 'TBN' && ['default', 'manager'].includes(tbnBranch(ctx))),
    groups: [
      {
        key: 'booking_customer',
        gate: isBookingCustomer,
        items: [
          { key: 'my_bookings', label: 'My Bookings', icon: 'fa-ticket', route: 'booking_customer_list_tbn',
            desc: 'Your bookings with live tracking and trip details.' },
        ],
      },
      {
        key: 'tbn',
        gate: ctx => isTbn(ctx) && !isBookingCustomer(ctx),
        items: [
          { key: 'actuals_tbn', label: 'Bookings & Actuals', shortLabel: 'Bookings', icon: 'fa-list-alt', route: 'actuals_tbn',
            desc: 'Review processed booking actuals matched against telematics.' },
          { key: 'driver_actuals_tbn', label: 'Driver Actuals', icon: 'fa-user-o', route: 'driver_actuals_tbn',
            desc: 'Driver booking timeline (HOS on → off) for the selected day.' },
          { key: 'vehicle_actuals_tbn', label: 'Vehicle Actuals', icon: 'fa-bus', route: 'vehicle_actuals_tbn',
            desc: 'Vehicle booking timeline (location start → finish) for the selected day.' },
          { key: 'troubleshooter_tbn', label: 'Actuals Troubleshooter', icon: 'fa-stethoscope', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by booking.',
            query: () => ({ source: 'tbn' }) },
          { key: 'stats_tbn', label: 'Actuals Stats', icon: 'fa-pie-chart', route: 'actuals_stats', query: () => ({ source: 'tbn' }),
            desc: 'Planned vs. actual summary statistics across bookings.' },
          { key: 'update_history', label: 'Booking Update History', icon: 'fa-history', route: 'booking_update_history',
            desc: 'History of booking record updates received from TBN over a date range.' },
          // overlapping bookings sharing a vehicle/driver window: two offset squares
          { key: 'overlap_updates_tbn', label: 'Overlap Updates', icon: 'fa-clone', route: 'overlap_updates_history',
            desc: 'History of overlap allocator changes to actuals for troubleshooting.' },
        ],
      },
      {
        // FI-2133: moovs reservations. no troubleshooter row -- that reads
        // actuals errors, and moovs has no actuals processor yet.
        key: 'moovs',
        gate: ctx => ctx.tenantType === 'MOOVS' && !isBookingCustomer(ctx),
        items: [
          { key: 'actuals_moovs', label: 'Bookings', shortLabel: 'Bookings', icon: 'fa-list-alt', route: 'actuals_moovs',
            desc: 'Reservations pulled from Moovs, by pickup or finish date.' },
        ],
      },
      {
        key: 'cm',
        gate: ctx => ctx.tenantType === 'CM' && !isBookingCustomer(ctx),
        items: [
          { key: 'actuals_cm', label: 'Bookings & Actuals', shortLabel: 'Bookings', icon: 'fa-list-alt', route: 'actuals',
            desc: 'Review processed booking actuals matched against telematics.' },
          { key: 'driver_actuals_cm', label: 'Driver Actuals', icon: 'fa-user-o', route: 'driver_actuals',
            desc: 'Driver booking timeline (HOS on → off) for the selected day.' },
          { key: 'vehicle_actuals_cm', label: 'Vehicle Actuals', icon: 'fa-bus', route: 'vehicle_actuals',
            desc: 'Vehicle booking timeline (location start → finish) for the selected day.' },
          { key: 'troubleshooter_cm', label: 'Actuals Troubleshooter', icon: 'fa-stethoscope', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by movement.',
            query: () => ({ source: 'cm' }) },
          { key: 'stats_cm', label: 'Actuals Stats', icon: 'fa-pie-chart', route: 'actuals_stats', query: () => ({ source: 'cm' }),
            desc: 'Planned vs. actual summary statistics across movements.' },
          // cm receives literal files, where tbn receives record updates -- so
          // this one is a file glyph and its tbn counterpart is fa-history
          { key: 'file_history', label: 'Booking File History', icon: 'fa-file-text-o', route: 'booking_file_list',
            desc: 'History of booking file updates, filterable by date range or type.' },
          { key: 'overlap_updates_cm', label: 'Overlap Updates', icon: 'fa-clone', route: 'overlap_updates_history',
            desc: 'History of overlap allocator changes to actuals for troubleshooting.' },
        ],
      },
      {
        key: 'fasttrak',
        gate: ctx => ctx.tenantType === 'FASTTRAK',
        items: [
          { key: 'actuals_ft', label: 'Bookings & Actuals', shortLabel: 'Bookings', icon: 'fa-list-alt', route: 'actuals_fasttrak',
            desc: 'Reservations matched to telematics after the scheduled end time.' },
          { key: 'troubleshooter_ft', label: 'Actuals Troubleshooter', icon: 'fa-stethoscope', route: 'actuals_troubleshooter',
            desc: 'Investigate actuals processing errors and warnings by reservation.',
            query: () => ({ source: 'fasttrak' }) },
        ],
      },
    ],
  },

  // the omnibus 'Fleet' section is gone: every group it held is now a
  // top-level rail section of its own (Alerts, Dispatch, Driver Manager,
  // Sync, VSM) or, where the group was a single destination, a leaf row
  // (Bus SOS, Driver Bid, Vehicle Departure Board). the group message sender
  // moved under Alerts. gates are carried over verbatim from the old groups
  // and items -- this is IA only, no audience changes. sections below with no
  // landingRoute still exist only through their items, so an empty one drops
  // out of the rail instead of rendering a dead row.

  {
    key: 'alerts',
    label: 'Alerts',
    icon: 'fa-bell-o',
    desc: 'Triggered fleet alerts, delivery history, and driver messaging.',
    surfacing: 'top',
    // landing resolves to the first visible item (no fixed home page): the
    // alerts dashboard is not reachable by every audience that sees the
    // group message sender
    gate: () => true,
    groups: [
      {
        key: 'alerts',
        items: [
          // develop's alerts refactor moved /alerts to a shell whose default
          // child is alerts_dashboard (navbar links here too) and dropped the
          // computed alertsRouteName. the FI-1869 merge left registry importing
          // that removed helper, so alertsRoute() called undefined -> resolveNav
          // threw and the whole horizon rail vanished. link the shell directly.
          { key: 'alerts', label: 'Alerts Dashboard', icon: 'fa-bell-o', route: 'alerts_dashboard',
            desc: 'Triggered fleet alerts and their history.',
            gate: ctx => ctx.tenantType === 'FASTTRAK' ? !isOpsFieldRole(ctx) : inDefaultBranch(ctx) },
          // the alerts shell's in-page rail is hidden under horizon/midnight
          // (it duplicated this nav), so its children need first-class entries
          // here or delivery failures becomes unreachable.
          // fa-archive, not fa-history: the history glyph indexes vsm history
          // elsewhere in the tree, and the dashboard row above owns the bell
          { key: 'alert_history', label: 'Alert History', shortLabel: 'Alerts', icon: 'fa-archive', route: 'alert_list',
            desc: 'Every alert sent, with delivery status.',
            gate: ctx => ctx.tenantType === 'FASTTRAK' ? !isOpsFieldRole(ctx) : inDefaultBranch(ctx) },
          // fa-ban: failure-shaped, and the exclamation triangle belongs to sos
          { key: 'delivery_failures', label: 'Delivery Failures', icon: 'fa-ban', route: 'delivery_failures',
            desc: 'Alert deliveries that bounced or failed.',
            gate: ctx => ctx.tenantType === 'FASTTRAK' ? !isOpsFieldRole(ctx) : inDefaultBranch(ctx) },
          // the legacy gate was dispatch/manager only, which left the row
          // invisible to every admin; hasGroupMessageAccess (admin,
          // customer-admin, dispatch, manager + alerts_enabled) is
          // the audience alerts_access.js has always defined and nothing ever
          // called, so it is wired up here. no new access: neither
          // group_message_sender route carries a router guard, so these roles
          // could always reach the pages by url -- this only links them.
          // the muggle-view guard keeps the registry's isEffectiveAdmin
          // convention: the helper's 'admin' arm must not survive view-as.
          { key: 'group_message', label: 'Group Messages', icon: 'fa-comments-o',
            desc: 'Send a group text message to drivers.',
            route: ctx => (isTbn(ctx) ? 'group_message_sender_tbn' : 'group_message_sender'),
            gate: ctx =>
              (ctx.tenantType === 'CM' || ctx.tenantType === 'TBN') &&
              !isTbnParent(ctx) &&
              !(ctx.role === 'admin' && !ctx.isEffectiveAdmin) &&
              hasGroupMessageAccess(ctx.user) },
          // FI-1953: mirrors the alerts_shell in-page rail's own shortcut
          // (classic-only) so horizon/midnight users get the same link --
          // same reasoning as delivery_failures/group_message above. gate
          // matches both the 'settings' section gate and the nested
          // 'alerts_settings' item gate below, since a user must clear both
          // to reach this tab via normal navigation anyway. shortcut: the
          // settings section owns this destination, so favorites, the star
          // and the current-section lookup resolve to its row, not this one
          { key: 'alert_settings', label: 'Alerts Settings', icon: 'fa-cog', route: 'settings',
            query: () => ({ tab: 'tab_alerts' }), shortcut: true,
            desc: 'Alert rules and thresholds for fleet events.',
            gate: ctx =>
              (ctx.isEffectiveAdmin || ctx.role === 'customer-admin') &&
              (ctx.isEffectiveAdmin ||
                isFeatureEnabled(ctx.features.vehicle_status_management_enabled) ||
                isFeatureEnabled(ctx.features.alerts_enabled)) },
        ],
      },
    ],
  },

  {
    key: 'dispatch',
    label: 'Dispatch',
    // fa-bus is the app's established vehicle glyph (vsm_shell, site_map_pages,
    // ops_desk) and was freed up when the Fleet section was split apart. note:
    // font-awesome 4.7 has no vehicle-with-driver glyph -- the driver half of
    // "assign a driver to a coach" is carried by the label, not the icon.
    icon: 'fa-bus',
    // the smart-dispatch group below is gated on an internal-only flag, so
    // most tenants reading this have the boards and nothing else. describe
    // what the section holds rather than implying a Dispatch product.
    desc: 'Live boards for bookings, vehicles, and driver assignments.',
    surfacing: 'top',
    // landing resolves to the first visible item (no fixed home page)
    gate: () => true, // visibility falls out of group/item gates; empty section drops
    groups: [
      {
        key: 'boards',
        label: 'Boards',
        icon: 'fa-columns',
        // the live boards rescued from reports > planned (and the jefferson
        // dashboards from misc): every row carries its old reports-section +
        // planned/misc-group predicates verbatim (reportsLinkGate +
        // show*Group), so who sees each board is byte-identical to when they
        // lived under reports
        items: [
          { key: 'driver_board', label: 'Driver Board', icon: 'fa-users', route: 'driver_board',
            desc: 'Real-time driver assignments and availability board.',
            gate: ctx => reportsLinkGate(ctx) && showPlannedGroup(ctx) &&
              !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'dispatch_board_tbn', label: 'Booking Dispatch Board', icon: 'fa-columns', route: 'dispatch_board_tbn',
            desc: 'Live dispatch board for booking management.',
            gate: ctx => reportsLinkGate(ctx) && showPlannedGroup(ctx) && isTbn(ctx) },
          { key: 'departure_board_tbn', label: 'Vehicle Departure Board', icon: 'fa-sign-out', route: 'trip_sheet_tbn',
            desc: 'Scheduled departure times and vehicle assignments.',
            gate: ctx => reportsLinkGate(ctx) && showPlannedGroup(ctx) && isTbn(ctx) },
          { key: 'departure_board_cm', label: 'Vehicle Departure Board', icon: 'fa-sign-out', route: 'trip_sheet',
            desc: 'Scheduled departure times and vehicle assignments.',
            gate: ctx => reportsLinkGate(ctx) && showPlannedGroup(ctx) &&
              !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'booking_monthly_tbn', label: 'Booking Monthly View', icon: 'fa-calendar', route: 'booking_monthly_view_tbn',
            desc: 'Monthly calendar view of all bookings.',
            gate: ctx => reportsLinkGate(ctx) && showPlannedGroup(ctx) && isTbn(ctx) },
          // jefferson lines live dashboards, out of the misc drawer
          { key: 'bus_tracker', label: 'Bus Tracker', icon: 'fa-bus', route: 'bus_tracker',
            desc: 'Live bus tracking view for Jefferson Lines.',
            gate: ctx => reportsLinkGate(ctx) && showMiscGroup(ctx) && ctx.tenant === TENANTS.JEFFERSON_LINES },
          { key: 'arrivals_departures', label: 'Arrive/Depart Dashboard', icon: 'fa-exchange', route: 'arrivals_departures_board',
            desc: 'Arrivals and departures live dashboard.',
            gate: ctx => reportsLinkGate(ctx) && showMiscGroup(ctx) && ctx.tenant === TENANTS.JEFFERSON_LINES },
        ],
      },
      {
        key: 'dispatch',
        // the smart-dispatch audience gate, pushed down from the section when
        // the boards moved in (boards are reports-package pages and must not
        // hide behind the smart flag). legacy dropdown lives in the cm default
        // branch (navbar.vue:297) and the tbn ops + default branches (320,
        // 367); cm ops never had it
        gate: ctx =>
          isFeatureEnabled(ctx.features.smart_dispatching_enabled) &&
          ((ctx.tenantType === 'CM' && cmBranch(ctx) === 'default') ||
            (ctx.tenantType === 'TBN' && ['ops', 'default'].includes(tbnBranch(ctx)))),
        items: [
          { key: 'smart_drivers', label: 'Smart Driver Mgmt', icon: 'fa-users',
            desc: 'Manage the driver list used for smart dispatching.',
            route: ctx => (isTbn(ctx) ? 'driver_list_tbn' : 'driver_list') },
          { key: 'smart_routes', label: 'Smart Route Assigner', icon: 'fa-random',
            desc: 'Smart route-to-driver assignment suggestions.',
            route: ctx => (isTbn(ctx) ? 'smart_route_assignments_tbn' : 'smart_route_assignments') },
          { key: 'trip_linker', label: 'Smart Trip Linker', icon: 'fa-link', route: 'trip_linker_report_tbn', gate: isTbn,
            desc: 'Link related trips for smarter dispatching.' },
        ],
      },
    ],
  },

  {
    // the section row keeps the full name -- it's where a new user learns
    // what the acronym means; the page item below (and so the favorites-bar
    // chip and site map card) abbreviates to 'VSM' to stay compact.
    key: 'vsm',
    label: 'Vehicle Status Manager',
    // fa-heartbeat is the app's vehicle-status glyph (the primary row, the
    // vsm board, and the settings tab all carry it); a boxed "VSM" text
    // lettermark was tried here and read as clutter next to real glyphs.
    // fa-wrench reads as maintenance-only and collides with admin tools.
    icon: 'fa-heartbeat',
    desc: 'Vehicle status, inspections, and maintenance.',
    surfacing: 'top',
    // landing resolves to the first visible item; which vsm page that is
    // depends on the tenant's enabled modes (vsmRoute below)
    gate: () => true,
    groups: [
      {
        key: 'vsm',
        items: [
          // fa-heartbeat, not fa-wrench: the wrench narrows to the maintenance
          // mode it literally names below, and two wrenches as siblings blur
          { key: 'vsm', label: 'VSM', icon: 'fa-heartbeat', route: vsmRoute,
            desc: 'Track vehicle status, inspections, and maintenance.',
            gate: vsmRoleGate },
          // the vsm has distinct mode pages (status board / dvir / maintenance);
          // the primary item above routes to the tenant's best mode
          // (vsmRouteName priority), these expose the OTHER enabled modes.
          // fasttrak stays on its single hardcoded dvir page.
          // spelled out: the acronym appears nowhere else a new user will have
          // seen first
          { key: 'vsm_dvir', label: 'Inspections (DVIR)', icon: 'fa-check-square-o',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_dvir_tbn' : 'vehicle_status_dvir'),
            desc: 'Driver vehicle inspection reports (DVIR).',
            gate: ctx => vsmModeGate(ctx, ctx.tenantType === 'TBN' ? 'vehicle_status_dvir_tbn' : 'vehicle_status_dvir',
              isFeatureEnabled(ctx.features.vehicle_status_management_dvir_enabled)) },
          // consolidated list of every active DVIR defect across the fleet, pulled
          // live from the ELD provider (Saucon). opt-in per tenant via
          // dvir_defects_page_enabled; its own standalone page, not a VSM board mode.
          { key: 'vsm_dvir_defects', label: 'DVIR Defects', icon: 'fa-exclamation-triangle',
            route: 'vehicle_defects',
            desc: 'All active DVIR defects across the fleet in one list.',
            // isDvirDefectsCapable is a COMPATIBILITY clause (telematics provider),
            // not an enablement flag, so admin demo mode honors it: the page shows
            // for staff on a Saucon account even before the flag is on, but never
            // on a non-Saucon account. See isDvirDefectsCapable.
            gate: ctx => vsmRoleGate(ctx) && isDvirDefectsCapable(ctx) &&
              isFeatureEnabled(ctx.features.dvir_defects_page_enabled) },
          { key: 'vsm_maint', label: 'Maintenance', icon: 'fa-wrench',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_maint_tbn' : 'vehicle_status_maint'),
            desc: 'Vehicle maintenance tracking and history.',
            gate: ctx => vsmModeGate(ctx, ctx.tenantType === 'TBN' ? 'vehicle_status_maint_tbn' : 'vehicle_status_maint',
              isFeatureEnabled(ctx.features.vehicle_status_management_maint_enabled)) },
          // timeline of upcoming bookings (plus TBN service calendar events)
          // alongside the wash/maint status columns; rides the base vsm flag
          // rather than its own mode flag. fasttrak has no upcoming page, and
          // FI-2133: neither does moovs -- VsmUpcoming reads BOOKING_TABLE, so
          // for a moovs tenant the page would load empty with no explanation.
          { key: 'vsm_upcoming', label: 'VSM Upcoming', icon: 'fa-calendar',
            route: ctx => (ctx.tenantType === 'TBN' ? 'vehicle_status_upcoming_tbn' : 'vehicle_status_upcoming'),
            desc: 'Upcoming vehicle schedules with wash and maintenance status.',
            gate: ctx => !['FASTTRAK', 'MOOVS'].includes(ctx.tenantType) && vsmRoleGate(ctx) &&
              isFeatureEnabled(ctx.features.vehicle_status_management_enabled) },
          // single home for vsm history: gate is the union of its two former
          // complementary declarations (fleet-side showVsmHistory, the retired
          // reports > misc row), so every user who saw it anywhere still does
          { key: 'vsm_history', label: 'VSM History', icon: 'fa-history', route: 'vehicle_status_history',
            desc: 'Historical vehicle status and inspection records.',
            gate: ctx => showVsmHistory(ctx) || showVsmHistoryReportSide(ctx) },
          // sits directly under vsm history and carries its gate verbatim: the two
          // answer the same question from opposite ends - what the board recorded,
          // and what the syncs did to it - so anyone who can see one wants the other
          { key: 'vsm_sync_history', label: 'Sync History', icon: 'fa-exchange', route: 'vehicle_sync_history',
            desc: 'Every vehicle status change made by the Fleetio, TBN calendar, DVIR and MaintainX syncs.',
            gate: ctx => showVsmHistory(ctx) || showVsmHistoryReportSide(ctx) },
          // moved from reports > misc to sit with its vsm family; gate carries
          // the old reports predicates verbatim so the audience is unchanged
          { key: 'maint_summary', label: 'Maintenance Summary', icon: 'fa-file-text-o', route: 'vehicle_maint_summary',
            desc: 'Summary of vehicle maintenance activities and costs.',
            gate: ctx => reportsLinkGate(ctx) && showMiscGroup(ctx) && fullReports(ctx) && !isTbn(ctx) &&
              isFeatureEnabled(ctx.features.vehicle_status_management_maint_summary_enabled) },
        ],
      },
      {
        // the read-only wall-display boards from the page's "More Info" modal.
        // not vue-router destinations: App.vue matches these fullPaths ahead
        // of the router and renders the chromeless board, so they are external
        // hrefs opened in a new tab, exactly as the modal opens them.
        key: 'vsm_standalone',
        label: 'Standalone Boards',
        items: vsmStandaloneItems(),
      },
    ],
  },

  {
    // FI-2018 consolidated the standalone Scorecard and Comparison pages into
    // Individual Driver / Compare tabs on the Dashboard. FI-2064 rebranded this
    // section to Driver Manager and split Individual Driver / Compare back out
    // into their own top-level pages under this same nav section -- still gated
    // by driver_scorecard_enabled (tenant config key, unchanged by the rebrand).
    // key stays 'driver_manager' (not the old 'scorecards') per the FI-2064
    // rename; any pre-rename persisted nav_state/favorites just stop rendering.
    key: 'driver_manager',
    label: 'Driver Manager',
    icon: 'fa-trophy',
    desc: 'Fleet-wide dashboard with leaderboards, individual driver profiles, comparisons, and flagged drivers.',
    surfacing: 'top',
    // both tenant types: the CM scorecard stack (cm_* endpoints +
    // CMScorecardProcessor) has existed alongside the TBN one all along
    // (2026-08-16, ben's call: scorecards work for both TBN and CM).
    // managers included: the pages already treat them as an audience
    // (driver_manager_individual's isCoachingRole lists 'manager'), the router
    // only turns drivers away, the site map files the cards under Reports for
    // them, and the dashboard widget's roles carry 'manager' -- the nav rail
    // was the one surface hiding it.
    gate: ctx => (inDefaultOrManagerBranch(ctx) || isSelfServiceScorecardDriver(ctx))
      && isFeatureEnabled(ctx.features.driver_scorecard_enabled),
    // no landingRoute: resolveSection falls back to the first rail item, which for
    // staff is the fleet Dashboard and for a driver is the only item their branch
    // leaves standing -- their own scorecard
    groups: [
      {
        key: 'driver_manager',
        items: [
          { key: 'driver_manager_dashboard', label: 'Dashboard', icon: 'fa-tachometer', route: 'driver_manager_dashboard',
            gate: ctx => !isSelfServiceScorecardDriver(ctx),
            desc: 'Fleet-wide dashboard with score distribution, tier charts, and flagged drivers.' },
          { key: 'driver_manager_roster_list', label: 'Roster List', icon: 'fa-list', route: 'driver_manager_roster_list',
            gate: ctx => !isSelfServiceScorecardDriver(ctx),
            desc: 'Sortable leaderboard of every driver with tier, combined score, and KPI columns.' },
          // the one page a driver sees, relabelled for them -- "Individual Driver"
          // reads as a picker over the roster, which is not what they get
          { key: 'driver_manager_individual', icon: 'fa-user', route: 'driver_manager_individual',
            label: ctx => (isSelfServiceScorecardDriver(ctx) ? 'My Scorecard' : 'Individual Driver'),
            desc: "View a single driver's full KPI profile and PDF report." },
          { key: 'driver_manager_compare', label: 'Compare', icon: 'fa-columns', route: 'driver_manager_compare',
            gate: ctx => !isSelfServiceScorecardDriver(ctx),
            desc: 'Compare KPIs side-by-side across an arbitrary number of drivers.' },
          // runs on the scorecard alone (combined score x miles), so it needs
          // no gate beyond the section's own. it does name other drivers, so it
          // stays out of the self-service driver branch.
          { key: 'driver_manager_risk_matrix', label: 'Risk Matrix', icon: 'fa-balance-scale', route: 'driver_manager_risk_matrix',
            gate: ctx => !isSelfServiceScorecardDriver(ctx),
            desc: 'Whether the miles are going to the right drivers — at-fault rate against mileage.' },
        ],
      },
    ],
  },

  {
    // FI-2070: the coaching family as its own product area — the dashboard
    // first (measurement), then the queue (action), then the two feeds.
    // item keys are the pre-split route names (coaching_portal /
    // coaching_stats) so favorites saved during the Driver Manager era
    // survive the move.
    key: 'coaching_manager',
    label: 'Coaching Portal',
    icon: 'fa-graduation-cap',
    desc: 'Driver coaching: dashboard, tickets, flagged drivers, and recognition.',
    surfacing: 'top',
    landingRoute: () => 'coaching_stats',
    // a coaching record is a personnel record: fleet admins, staff, and
    // managers only, gated per tenant by coaching_portal_enabled. there is no
    // staff bypass — the flag governs for everyone, staff included, so the
    // section is absent on a tenant that has not turned coaching on rather
    // than appearing for support on every fleet. the manager BRANCH is
    // admitted alongside default because the manager ROLE is allowed —
    // a section gate that excluded the branch would make that unreachable.
    gate: ctx => (
      (CM_LIKE_TYPES.includes(ctx.tenantType) && ['manager', 'default'].includes(cmBranch(ctx))) ||
      (ctx.tenantType === 'TBN' && ['manager', 'default'].includes(tbnBranch(ctx)))
    ) && ['admin', 'customer-admin', 'customer', 'manager'].includes(ctx.role)
      && isFeatureEnabled(ctx.features.coaching_portal_enabled),
    groups: [
      {
        key: 'coaching_manager',
        items: [
          { key: 'coaching_stats', label: 'Dashboard', icon: 'fa-tachometer', route: 'coaching_stats',
            desc: 'Coaching KPIs: effectiveness, workload, sources, outcomes, and trends.' },
          { key: 'coaching_board', label: 'Coaching Board', icon: 'fa-columns', route: 'coaching_board',
            desc: 'The queue as swim lanes — drag tickets between statuses.' },
          { key: 'coaching_portal', label: 'Coaching Tickets', icon: 'fa-check-square-o', route: 'coaching_portal',
            desc: 'Work the coaching queue: assignments, sign-offs, and the append-only record.' },
          // the two scorecard-fed pages. without the scorecard flag no rows
          // are ever pre-calculated for the tenant, so these would render
          // empty forever -- hide them and let coaching run on manually
          // raised tickets. see utils/coaching_access.js.
          { key: 'coaching_flagged', label: 'Flagged Drivers', icon: 'fa-flag-o', route: 'coaching_flagged',
            desc: 'Drivers over a coaching threshold this period — assign coaching from here.',
            gate: ctx => hasScorecardCoachingPages(ctx.features) },
          { key: 'coaching_recognition', label: 'Recognition Candidates', icon: 'fa-star-o', route: 'coaching_recognition',
            desc: 'Top performers worth recognizing — the positive mirror of the flags.',
            gate: ctx => hasScorecardCoachingPages(ctx.features) },
        ],
      },
    ],
  },

  // the Sync section (Compare Driver Lists/Profiles/History, data_services_compare_drivers_enabled)
  // was removed from the left nav (2026-09-29): the feature is deprecated and disabled for all
  // customers, so it is not offered even under admin demo mode. The routes stay defined so any
  // historical link still resolves; nothing advertises them. Its PACKAGE_GATES.sync entry is gone too.

  {
    // the sos page (send a request + history) is an ordinary leaf row; the
    // top-bar red pill is a separate ALARM item that fi_top_nav only renders
    // while an sos request is actually open (it polls history -- there is no
    // active-sos store; legacy showed a permanent nav link). the alarm item
    // has to stay inside a group here: resolveTopBarItems walks the raw
    // sections, not the rail tree.
    key: 'sos',
    label: 'Bus SOS',
    // the warning triangle is the sos glyph everywhere else (the leaf rows
    // and the top bar's red alarm pill), so the section carries it too; a
    // boxed "SOS" text lettermark was tried and read as clutter.
    icon: 'fa-exclamation-triangle',
    desc: 'Send a Bus SOS request and review past requests.',
    surfacing: 'top',
    landingRoute: () => 'sos_dashboard',
    gate: ctx => isFeatureEnabled(ctx.features.bus_sos_enabled) && !isBookingCustomer(ctx) && !isDriver(ctx),
    groups: [
      {
        key: 'sos',
        items: [
          // the Bus SOS home: open requests, network reach, yard counts, recent activity
          { key: 'sos_dashboard', label: 'SOS Dashboard', shortLabel: 'Dashboard', icon: 'fa-tachometer', route: 'sos_dashboard' },
          // SOS this company raised (base Bus SOS); SOS received from other companies (opt-in)
          { key: 'sos_sent_history', label: 'SOS Sent History', shortLabel: 'Sent', icon: 'fa-history', route: 'sos_sent_list' },
          { key: 'sos_received_history', label: 'SOS Received History', shortLabel: 'Received', icon: 'fa-inbox', route: 'sos_received_list',
            gate: ctx => isFeatureEnabled(ctx.features.bus_sos_receive_from_other_companies) },
          { key: 'sos_map', label: 'SOS Yard Map', icon: 'fa-map-marker', route: 'sos_map_page' },
          { key: 'sos_send', label: 'Send SOS', icon: 'fa-paper-plane', route: 'sos_send' },
          // the top-bar red pill is a separate ALARM item that fi_top_nav only
          // renders while an sos request is actually open (it polls history);
          // resolveTopBarItems walks the raw sections, so it has to live here.
          { key: 'sos_alarm', label: 'SOS', icon: 'fa-exclamation-triangle', route: 'sos_list',
            placement: 'topbar' },
        ],
      },
    ],
  },

  {
    // hardcoded dattco gate (legacy oddity, see TENANTS). the two pages have
    // disjoint audiences -- drivers bid, staff manage -- so the section is a
    // leaf whose landing follows the role instead of a one-row subtree.
    key: 'driver_bid',
    label: 'Driver Bid',
    icon: 'fa-gavel',
    desc: 'Open driver work postings, bids, and awards.',
    surfacing: 'top',
    landingRoute: ctx => (ctx.role === 'driver' ? 'driver_bid_board_tbn' : 'driver_bid_manage_board_tbn'),
    landingOnly: true,
    gate: ctx => ctx.tenant === TENANTS.DATTCO &&
      (ctx.role === 'driver' || ctx.isEffectiveAdmin || ctx.role === 'customer-admin'),
    groups: [],
  },

  {
    // cm drivers' only board (navbar.vue:267)
    key: 'departure_board',
    label: 'Vehicle Departure Board',
    icon: 'fa-clock-o',
    desc: 'Scheduled departure times and vehicle assignments.',
    surfacing: 'top',
    landingRoute: () => 'trip_sheet_standalone',
    landingOnly: true,
    gate: ctx => ctx.tenantType === 'CM' && ctx.role === 'driver',
    groups: [],
  },

  {
    // FI-1929: erm customer rollout. staff now get this rail destination too
    // (it used to exclude effective-admins, who reached the erm through the
    // admin tools preview group; that duplicate entry is gone). staff pass
    // unconditionally -- the preview entry never checked the tenant flag, so
    // gating them on it here would have cost them access. non-staff need the
    // tenant flag plus an allowed role -- mirrors the classic navbar gate and
    // the router guard for ops_desk. muggle-view admins keep role 'admin'
    // (isEffectiveAdmin false), so they see the customer-side gate.
    // key stays 'erm' so persisted nav_state survives the product rename
    key: 'erm',
    label: 'Event Response Manager',
    shortLabel: 'ERM',
    // the section's landing IS the event log, and starring that page saves
    // the section destination (its tenant-pinned landing outscores the bare
    // erm_log item) -- so the favorite must say what it opens
    favLabel: 'ERM Event Log',
    // fa-flag: an event here is something RAISED for the ops desk's
    // attention (incident, kudos, note), and the flag is the raised-marker
    // glyph -- fa-clipboard before it read as generic paperwork. naming
    // history: 'Event Response Manager'/'ERM' -> 'Ops Events' (2026-07-23)
    // -> 'Ops Response Manager' (2026-07-28) -> back to 'Event Response
    // Manager' (2026-08-03, ben's call after evan's push-back: "events" is
    // the hook customers reach for; singular per the original name); the
    // app's own pages (ops_desk.vue, admin_shell.vue, the public form)
    // carry the same rename, and records stay 'ops events'.
    icon: 'fa-flag',
    desc: 'Log and work issues, kudos, and notes with distribution and the Huddle recap.',
    surfacing: 'top',
    landingRoute: () => 'ops_desk',
    landingQuery: ctx => ({ tenant: ctx.tenant }),
    // the audience lives in ERM_ACCESS_ROLES so this entry, the router guard,
    // the site map and the dashboard widget can't drift apart
    // no staff bypass here, unlike the router guard: ops_desk_enabled governs
    // for everyone, so the section is simply absent on a tenant that has not
    // bought ERM rather than appearing for support on every fleet.
    gate: ctx => hasErmNavAccess(ctx.role, ctx.features),
    // the log and analytics were in-page tabs; the rail lists them as real
    // destinations so each is deep-linkable, starrable, and reachable from
    // the ask box / site map (2026-08-01, follows the 2026-07-31 orm
    // review). items skip the tenant query on purpose -- rail items never
    // pin tenant (see maps/reports), and a pinned tenant would break
    // isItemActive on tenant-less urls. "event log", not "events log":
    // attributive nouns stay singular.
    groups: [
      {
        items: [
          // dashboard first (2026-08-23, ben's call): the trends view leads
          // the section. key stays 'erm_analytics' so saved favorites and
          // persisted nav_state survive the renames ('ERM Dashboard', then
          // the bare 'Dashboard' every section child uses).
          { key: 'erm_analytics', label: 'Dashboard', favLabel: 'ERM Dashboard', icon: 'fa-tachometer',
            route: 'ops_desk', query: () => ({ view: 'analytics' }),
            desc: 'Event trends and breakdowns over time.' },
          { key: 'erm_log', label: 'Event Log', favLabel: 'ERM Event Log', icon: 'fa-list-ul',
            route: 'ops_desk',
            desc: 'The running log of issues, kudos, and notes.' },
          // no gate of its own: the board is the same events as the log, so
          // it rides the section's ops_desk_enabled + role gate exactly like
          // the log and the dashboard rows do.
          { key: 'erm_board', label: 'Event Board', favLabel: 'ERM Event Board', icon: 'fa-columns',
            route: 'erm_board',
            desc: 'The event log as swim lanes — drag events between statuses.' },
          // ERM settings live with ERM (2026-10-01, chaz): the row moved here
          // from Settings, and the Event Log's and Event Board's gear and the
          // settings modal retired for it. the page is still the settings
          // shell's tab (tenant_config_erm.vue), so the row addresses
          // ?tab=tab_erm, and a favorite (keyed by route + query) survives the
          // move. the gate is the Settings section's own, since this section
          // also reaches staff ('customer') and managers: the erm admin tier.
          { key: 'erm_settings', label: 'Settings', favLabel: 'ERM Settings', icon: 'fa-cog',
            route: 'settings', query: () => ({ tab: 'tab_erm' }),
            desc: 'Categories, recipients, the public form, and the huddle.',
            gate: ctx => ctx.isEffectiveAdmin || ctx.role === 'customer-admin' },
        ],
      },
    ],
  },

  {
    key: 'reports',
    label: 'Reports',
    icon: 'fa-bar-chart',
    desc: 'Actuals, planned, on-time, and other reporting.',
    surfacing: 'top',
    landingRoute: () => 'report_dashboard',
    gate: reportsLinkGate,
    groups: [
      {
        key: 'actuals',
        label: 'Actuals',
        icon: 'fa-check-square-o',
        gate: showActualsGroup,
        items: [
          // tbn list (report_shell.vue:187-209)
          { key: 'otp_daily_tbn', label: 'Daily On-Time Report', icon: 'fa-clock-o', route: 'actuals_perf_report_tbn',
            desc: 'Compare planned booking times against actual on-time performance by day.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          // "trends across a date range" is the whole difference from the daily
          // report above, so the glyph carries it
          { key: 'otp_multiday_tbn', label: 'Multiday On-Time Report', icon: 'fa-line-chart', route: 'actuals_multiday_otp_report_tbn',
            desc: 'On-time performance trends across a custom date range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'on_time_pct_tbn', label: 'Driver On-Time % Report', icon: 'fa-percent', route: 'actuals_on_time_report_tbn',
            desc: 'On-time gates (to yard, depart yard) by driver for a date range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'pickup_dropoff_tbn', label: 'Pickup/Dropoff Report', icon: 'fa-exchange', route: 'actuals_billing_report_tbn',
            desc: 'Pickup and dropoff event details per booking.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'start_events_tbn', label: 'Start Events Report', icon: 'fa-play-circle-o', route: 'actuals_start_events_report_tbn',
            desc: 'Vehicle start events matched to scheduled bookings.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          { key: 'driver_daily_tbn', label: 'Driver Daily Report', icon: 'fa-user-o', route: 'actuals_driver_payroll_report_tbn',
            desc: 'Driver activity and hours for a single day.',
            gate: isTbn }, // also the restricted manager/tbn-parent list
          { key: 'driver_multiday_tbn', label: 'Driver Multiday Report', icon: 'fa-users', route: 'actuals_driver_multiday_report_tbn',
            desc: 'Driver activity and hours across multiple days.',
            gate: isTbn },
          { key: 'payroll_summary_tbn', label: 'Payroll Summary Report', icon: 'fa-money', route: 'actuals_payroll_summary_report_tbn',
            desc: 'Aggregated payroll totals by driver for a date range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) },
          // FI-2167: revived for ebmeyer only. pulled from the legacy dropdown in
          // 2026-04 over performance (one synchronous samsara trips call per
          // vehicle), so keep it off larger fleets until that is addressed
          { key: 'mileage_daily_tbn', label: 'Actual Mileage Report', icon: 'fa-road', route: 'actuals_mileage_tbn',
            desc: 'Miles per vehicle per day, from bookings vs. vehicle odometer, over a day or a range.',
            gate: ctx => isTbn(ctx) && fullReports(ctx) && ctx.tenant === TENANTS.EBMEYER },
          // cm list (report_shell.vue:194-219)
          { key: 'otp_daily_cm', label: 'Daily On-Time Report', icon: 'fa-clock-o', route: 'actuals_perf_report',
            desc: 'Compare planned booking times against actual on-time performance by day.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'otp_multiday_cm', label: 'Multiday On-Time Report', icon: 'fa-line-chart', route: 'actuals_multiday_otp_report',
            desc: 'On-time performance trends across a custom date range.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'on_time_pct_cm', label: 'Driver On-Time % Report', icon: 'fa-percent', route: 'actuals_on_time_report',
            desc: 'On-time gates (to yard, depart yard, to pickup) by driver for a date range.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'mileage_by_booking', label: 'Mileage By Booking', icon: 'fa-road', route: 'actuals_mileage_by_booking_report',
            desc: 'Per-booking mileage breakdown for accurate client billing.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          // billing (invoicing the client) vs payroll (paying the driver): the
          // two money rows sit in this same folder, so they take different coins
          { key: 'billing_cm', label: 'Billing Report', icon: 'fa-usd', route: 'actuals_billing_report',
            desc: 'Summarised billing data based on completed actuals.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'vehicle_start_stop', label: 'Vehicle Start/Stop', icon: 'fa-power-off', route: 'actuals_vehicle_report',
            desc: 'Vehicle engine start and stop events per booking.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          { key: 'driver_daily_cm', label: 'Driver Daily Report', icon: 'fa-user-o', route: 'actuals_driver_payroll_report',
            desc: 'Driver activity and hours for a single day.',
            gate: ctx => !isTbn(ctx) },
          { key: 'driver_multiday_cm', label: 'Driver Multiday Report', icon: 'fa-users', route: 'actuals_driver_multiday_report',
            desc: 'Driver activity and hours across multiple days.',
            gate: ctx => !isTbn(ctx) },
          { key: 'payroll_summary_cm', label: 'Payroll Summary Report', icon: 'fa-money', route: 'actuals_payroll_summary_report',
            desc: 'Aggregated payroll totals by driver for a date range.',
            gate: ctx => !isTbn(ctx) && fullReports(ctx) },
          // mtr western's hours reports are actuals_* routes; they lived in
          // misc by accident of the legacy shell. NOTE the move adds this
          // folder's showActualsGroup condition (actuals_enabled), which the
          // old misc home did not require -- accepted, mtr runs actuals
          { key: 'hours_by_client', label: 'Hours By Client Report', icon: 'fa-building-o', route: 'actuals_hours_by_client_report_tbn',
            desc: 'Total hours worked grouped by client.',
            gate: ctx => fullReports(ctx) && ctx.tenant === TENANTS.MTR_WESTERN },
          { key: 'hours_by_booking', label: 'Hours By Booking Report', icon: 'fa-calendar-o', route: 'actuals_hours_by_booking_report_tbn',
            desc: 'Hours breakdown per individual booking.',
            gate: ctx => fullReports(ctx) && ctx.tenant === TENANTS.MTR_WESTERN },
        ],
      },
      {
        key: 'planned',
        label: 'Planned',
        icon: 'fa-calendar-o',
        gate: showPlannedGroup,
        items: [
          // this folder runs daily/weekly/monthly for both drivers and
          // vehicles. the granularity is already spelled out in every label,
          // so the glyphs index the SUBJECT instead: one glance separates the
          // driver block from the vehicle block in a ten-row list.
          // tbn list (report_shell.vue:226-239)
          { key: 'driver_util_daily_tbn', label: 'Driver Utilization Daily', icon: 'fa-user-o', route: 'planned_drivers_daily_tbn', gate: isTbn,
            desc: 'Daily view of driver utilisation against planned bookings.' },
          { key: 'driver_util_weekly_tbn', label: 'Driver Utilization Weekly', icon: 'fa-user-o', route: 'planned_drivers_weekly_util_tbn', gate: isTbn,
            desc: 'Weekly driver utilisation across the fleet.' },
          { key: 'driver_util_monthly_tbn', label: 'Driver Utilization Monthly', icon: 'fa-user-o', route: 'planned_driver_types_monthly_tbn', gate: isTbn,
            desc: 'Monthly breakdown of driver type utilisation.' },
          { key: 'driver_dispatch_weekly_tbn', label: 'Driver Dispatch Weekly', icon: 'fa-paper-plane-o', route: 'planned_drivers_weekly_dispatch_tbn', gate: isTbn,
            desc: 'Weekly driver dispatch schedule.' },
          { key: 'vehicle_util_daily_tbn', label: 'Vehicle Utilization Daily', icon: 'fa-bus', route: 'planned_vehicle_daily_tbn', gate: isTbn,
            desc: 'Daily vehicle usage compared to planned schedule.' },
          { key: 'vehicle_util_weekly_tbn', label: 'Vehicle Utilization Weekly', icon: 'fa-bus', route: 'planned_vehicle_weekly_tbn', gate: isTbn,
            desc: 'Weekly view of vehicle fleet utilisation.' },
          { key: 'vehicle_util_monthly_tbn', label: 'Vehicle Utilization Monthly', icon: 'fa-bus', route: 'planned_vehicle_types_monthly_tbn', gate: isTbn,
            desc: 'Monthly fleet utilisation by vehicle type.' },
          // the live boards (departure/dispatch/monthly/driver) moved to
          // dispatch > boards -- planned keeps only the utilization reports
          // cm list (report_shell.vue:223-248); vehicle weekly is also the
          // garage-only item, so it skips the garage exclusion
          { key: 'driver_util_daily_cm', label: 'Driver Utilization Daily', icon: 'fa-user-o', route: 'planned_drivers_daily',
            desc: 'Daily view of driver utilisation against planned bookings.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'driver_util_monthly_cm', label: 'Driver Utilization Monthly', icon: 'fa-user-o', route: 'planned_driver_types_monthly_cm',
            desc: 'Monthly breakdown of driver type utilisation.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'vehicle_util_daily_cm', label: 'Vehicle Utilization Daily', icon: 'fa-bus', route: 'planned_vehicle_daily',
            desc: 'Daily vehicle usage compared to planned schedule.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
          { key: 'vehicle_util_weekly_cm', label: 'Vehicle Utilization Weekly', icon: 'fa-bus', route: 'planned_vehicle_weekly',
            desc: 'Weekly view of vehicle fleet utilisation.',
            gate: ctx => !isTbn(ctx) },
          { key: 'vehicle_util_monthly_cm', label: 'Vehicle Utilization Monthly', icon: 'fa-bus', route: 'planned_vehicle_types_monthly',
            desc: 'Monthly fleet utilisation by vehicle type.',
            gate: ctx => !isTbn(ctx) && reportsRestrictedRole(ctx) !== 'garage' },
        ],
      },
      {
        // spelled out: the initialism collided with the on-time reports in
        // the actuals folder, which users read as the "real" otp reports
        key: 'otp',
        label: 'On-Time and Routes',
        icon: 'fa-clock-o',
        gate: showOtpGroup,
        items: [
          // "on-time" prefix keeps this distinguishable from maps > vehicle
          // route history (a different page) in the rail and in favorites
          { key: 'route_history_report', label: 'On-Time Route History', icon: 'fa-road', route: 'vehicle_route_history',
            desc: 'Historical route data for each vehicle.' },
          { key: 'route_assignments', label: 'Route Assignments', icon: 'fa-bus', route: 'route_assignments',
            desc: 'Route-to-vehicle assignment history.',
            gate: ctx => !isTbn(ctx) },
          { key: 'route_addresses', label: 'Route Addresses', icon: 'fa-map-marker', route: 'route_addresses',
            desc: 'Address list for configured routes.' },
          // it runs overnight, and the moon is the one glyph that says so
          { key: 'otp_nightly', label: 'On-Time Nightly Run History', icon: 'fa-moon-o', route: 'report_otp',
            desc: 'Nightly on-time report run history and results.' },
          { key: 'otp_results', label: 'On-Time Results History', icon: 'fa-history', route: 'otp_history',
            desc: 'Historical on-time results with trends over time.' },
        ],
      },
      {
        // "misc" was an anti-label; what remains after the boards, vsm rows,
        // and mtr hours reports moved out is genuinely miscellaneous overflow
        key: 'misc',
        label: 'Other',
        icon: 'fa-ellipsis-h',
        gate: showMiscGroup,
        items: [
          // "unplanned" is the point of both rows: activity that fell OUTSIDE
          // the schedule, which is what the struck-through glyphs say
          { key: 'unplanned_vehicle', label: 'Unplanned Vehicle Activity', icon: 'fa-calendar-times-o', route: 'report_unplanned_vehicle',
            desc: 'Vehicles that moved outside of scheduled bookings.',
            gate: ctx => !isTbn(ctx) },
          { key: 'unplanned_driver', label: 'Unplanned Driver Activity', icon: 'fa-user-times', route: 'report_unplanned_hos',
            desc: 'Driver activity recorded outside planned bookings.',
            gate: ctx => !isTbn(ctx) },
          // the one misc item tbn-parent keeps (report_shell.vue:263-265)
          { key: 'event_notes', label: 'Event Notes History', icon: 'fa-sticky-note-o', route: 'event_notes_list',
            desc: 'Notes and annotations recorded against fleet events.' },
          { key: 'hos_history', label: 'HOS Event History', icon: 'fa-clock-o', route: 'hos_history_report',
            desc: 'Hours of service events for equipped vehicles.',
            gate: ctx => fullReports(ctx) && (isTbn(ctx) || ctx.telematicsType === 'SAMSARA') },
          { key: 'tyler_routes', label: 'Tyler Route Sync', icon: 'fa-cogs', route: 'tyler_route_processor',
            desc: 'Process and sync Tyler route data.',
            gate: ctx => fullReports(ctx) && isTbn(ctx) && isFeatureEnabled(ctx.features.tbn_tyler_route_sync_enabled) },
          { key: 'report_converter', label: 'Report Converter', icon: 'fa-retweet', route: 'report_converter',
            desc: 'Convert report formats for third-party system compatibility.',
            gate: ctx => fullReports(ctx) && isFeatureEnabled(ctx.features.views_reports_converter_enabled) },
          // legacy used raw role==='admin' here; normalized to isEffectiveAdmin
          { key: 'intrastate', label: 'Intrastate Booking Report', icon: 'fa-map-o', route: 'intrastate_booking_report_tbn',
            desc: 'Report on intrastate travel bookings.',
            gate: ctx => fullReports(ctx) && isTbn(ctx) && (ctx.tenant === TENANTS.ANDERSON || ctx.isEffectiveAdmin) },
          { key: 'hale_daily_dispatch', label: 'Daily Dispatch', icon: 'fa-clipboard', route: 'hale_daily_dispatch_report_tbn',
            desc: 'Daily dispatch sheet split by garage, with departing and already-out trips.',
            gate: ctx => fullReports(ctx) && isTbn(ctx) && (ctx.tenant === TENANTS.HALE || ctx.isEffectiveAdmin) },
        ],
      },
    ],
  },

  {
    // chaz round 13: help gets a real home in the tree, above settings. it
    // also carries the fleet assistant LAUNCH row, so phones -- which lost
    // the floating pill to the bar button and never had the desktop help
    // menu -- reach the help center, the legal pages, and the assistant
    // from the drawer. the top-bar help menu keeps its tab-level shortcuts
    // on desktop.
    key: 'help',
    label: 'Help',
    icon: 'fa-question-circle',
    desc: 'Help center, legal pages, and the Fleet Assistant.',
    surfacing: 'utility',
    gate: () => true,
    groups: [
      {
        // per-tab rows (round 17, chaz): the help center's tabs are
        // first-class rail destinations under horizon, and the in-page tab
        // strip hides there. help_center.vue pins ?tab= as the url source
        // of truth under horizon (incl. articles, whose classic clean-url
        // delete is horizon-suppressed) so these pinned queries match
        // strictly. same keys/gates as HELP_MENU_GROUPS below.
        key: 'help_center',
        // default-on explicit-false pattern (navbar.vue:18-22). locked
        // roles are excluded: their router whitelists bounce /help_center
        gate: ctx => !isLockedRole(ctx) &&
          (ctx.isEffectiveAdmin || ctx.features.help_center_enabled !== false),
        items: [
          // same glyphs as HELP_MENU_GROUPS below: one destination, one icon,
          // whichever surface the user reaches it from
          { key: 'help_articles', label: 'Help Articles', icon: 'fa-book', route: 'help_center',
            query: () => ({ tab: 'articles' }),
            desc: 'How-to articles for every feature.' },
          // non-admins additionally need the company-docs flag
          // (help_center.vue:2877-2881)
          { key: 'company_docs', label: 'Company Docs', icon: 'fa-file-text-o', route: 'help_center',
            query: () => ({ tab: 'company' }),
            desc: 'Documents your company shares with portal users.',
            gate: ctx => (ctx.isEffectiveAdmin || ctx.features.company_documents_in_help_center !== false) && !isYardOnly(ctx) },
          { key: 'support_tickets', label: 'Support Tickets', shortLabel: 'Tickets', icon: 'fa-ticket', route: 'help_center',
            query: () => ({ tab: 'support' }),
            desc: 'Open and track support requests.' },
          // the same tickets as swim lanes (erm_board / coaching_board
          // shape). staff only: the lanes and the queue rank are Initek's
          // triage view, and the router bounces everyone else.
          { key: 'support_ticket_board', label: 'Ticket Board', shortLabel: 'Board', favLabel: 'Tickets', icon: 'fa-columns', route: 'support_ticket_board',
            desc: 'Support tickets as swim lanes — drag to change status and rank.',
            gate: ctx => ctx.isEffectiveAdmin },
        ],
      },
      {
        key: 'help',
        items: [
          // an action, not a route: opens the assistant panel in place (the
          // full-page fa keeps its own item under fleet > dispatch). same
          // gate as the launcher pill / bar button, minus the route check --
          // rows must not vanish from the tree as the user navigates.
          { key: 'fleet_assistant_launch', label: 'Fleet Assistant', icon: 'fa-magic', action: 'open-fleet-assistant',
            desc: 'Ask the Fleet Assistant about your fleet\'s data.',
            gate: ctx => faLauncherAvailable(ctx.user, null) },
          // the classic bar carried this as a top-bar icon alongside admin
          // tools / settings / help center; it was the one destination the
          // horizon rail never picked up. locked roles are excluded -- their
          // router whitelists bounce /site_map -- and so are drivers, whose
          // handful of pages don't want a tour of the whole product (the guard
          // in router/index.js bounces them too, since most driver tenants have
          // no route whitelist).
          { key: 'site_map', label: 'Site Map', icon: 'fa-sitemap', route: 'site_map',
            desc: 'Explore everything Fleet Integrations offers for your account.',
            gate: ctx => !isLockedRole(ctx) && !isDriver(ctx) },
          { key: 'terms', label: 'Terms of Service', icon: 'fa-gavel', external: true, href: () => LEGAL_URLS.terms },
          { key: 'privacy', label: 'Privacy Policy', icon: 'fa-lock', external: true, href: () => LEGAL_URLS.privacy },
        ],
      },
    ],
  },

  {
    // cross-system data checks for a TBN account (TBN vs Fleet Int vs telematics vs Fleetio).
    // One standalone subpage per domain rather than one crowded page.
    key: 'data_sync',
    label: 'Data Sync',
    icon: 'fa-exchange',
    desc: 'Cross-system data lookup differences for this TBN account.',
    // top (main) area, directly under Reports -- Reports is the last 'top' section and Data Sync
    // follows it in this array, so it renders immediately beneath Reports in the left nav.
    surfacing: 'top',
    landingRoute: () => 'data_sync_yards',
    // staff admins and the tenant's own customer-admins, on TBN accounts
    gate: ctx => (ctx.isEffectiveAdmin || ctx.role === 'customer-admin') && isTbn(ctx),
    groups: [
      {
        key: 'data_sync',
        items: [
          { key: 'data_sync_yards', label: 'Yards', icon: 'fa-map-o', route: 'data_sync_yards',
            desc: 'Yards in TBN vs Fleet Int, yards missing a Samsara geofence, and buses parked away from their yard.' },
          { key: 'data_sync_vehicles', label: 'Vehicles', icon: 'fa-bus', route: 'data_sync_vehicles',
            desc: 'Vehicles with no telematics link, and differences between TBN, telematics and Fleetio.' },
          { key: 'data_sync_drivers', label: 'Drivers', icon: 'fa-id-card-o', route: 'data_sync_drivers',
            desc: 'Drivers in TBN vs the telematics roster.' },
        ],
      },
    ],
  },

  {
    key: 'settings',
    label: 'Settings',
    icon: 'fa-cog',
    desc: 'Account configuration and user management.',
    surfacing: 'utility',
    // items address the shell's v-show tabs via ?tab= (tenant_config_shell
    // watches the query under horizon) so its unsaved-changes guard keeps
    // working. the flat tenant_config_* routes bypass that guard -- never
    // link those from the rail. the landing is the shell's horizon-only
    // overview pseudo-tab (a card grid of the sections), so /settings no
    // longer drops users straight into General.
    landingRoute: () => 'settings',
    landingQuery: () => ({ tab: 'tab_overview' }),
    // legacy gear used raw role and survived muggle preview; normalized
    gate: ctx => ctx.isEffectiveAdmin || ctx.role === 'customer-admin',
    groups: [
      // favLabel: a favorite lives outside the Settings heading, so the
      // favorites surfaces say which settings it opens ('Alerts Settings');
      // the tree and flyout sit under that heading and keep the bare label.
      // rows whose label already says Settings, and the Manage Users task row
      // (seeded default favorite whose chip is its shortLabel), stay bare.
      {
        // Every settings row carries `ctx.isEffectiveAdmin ||` on its gate: staff
        // see every settings page whether or not the account bought the package,
        // because they are the people who turn one on. Reverses the 2026-09-10
        // decision for the settings rows only -- PACKAGE_GATES still hide whole
        // rail sections, and isEffectiveAdmin is false in muggle preview, so a
        // customer's nav and the preview of it are unchanged.
        key: 'tabs',
        // twelve rows in one flat group, so every glyph here has to earn its
        // place. each one mirrors the icon its subject carries elsewhere in
        // the tree (alerts fa-bell-o, vsm fa-heartbeat, assistant fa-magic),
        // which turns settings into a map of the app rather than a fresh set
        // of symbols to learn.
        items: [
          { key: 'general', label: 'General', favLabel: 'General Settings', icon: 'fa-building-o', route: 'settings', query: () => ({ tab: 'tab_general' }),
            desc: 'Company profile, branding, and core account details.' },
          { key: 'bookings_actuals', label: 'Bookings & Actuals', favLabel: 'Bookings & Actuals Settings', icon: 'fa-calendar-check-o', route: 'settings', query: () => ({ tab: 'tab_bookings_actuals' }),
            desc: 'How bookings import and match against recorded actuals.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.actuals_enabled) },
          { key: 'map_settings', label: 'Map Settings', icon: 'fa-map-marker', route: 'settings', query: () => ({ tab: 'tab_cust_map_views' }),
            desc: 'Default map views, labels, and visibility options.',
            // yard-only accounts have no Fleet Map to configure (only the SOS Yard Map)
            gate: ctx => ctx.isEffectiveAdmin || !isYardOnly(ctx) },
          { key: 'views_reports', label: 'Views & Reports', favLabel: 'Views & Reports Settings', icon: 'fa-bar-chart', route: 'settings', query: () => ({ tab: 'tab_views_reports' }),
            desc: 'Which report pages are available and how they behave.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.views_reports_enabled) },
          { key: 'alerts_settings', label: 'Alerts', favLabel: 'Alerts Settings', icon: 'fa-bell-o', route: 'settings', query: () => ({ tab: 'tab_alerts' }),
            desc: 'Alert rules and thresholds for fleet events.',
            gate: ctx => ctx.isEffectiveAdmin ||
              isFeatureEnabled(ctx.features.vehicle_status_management_enabled) ||
              isFeatureEnabled(ctx.features.alerts_enabled) },
          // alerts are the rules that fire; notifications are what gets sent
          { key: 'notifications_settings', label: 'Notifications', favLabel: 'Notifications Settings', icon: 'fa-envelope-o', route: 'settings', query: () => ({ tab: 'tab_notifications' }),
            desc: 'Notification templates and recipient settings.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.alerts_enabled) },
          { key: 'vsm_settings', label: 'Vehicle Status', favLabel: 'Vehicle Status Settings', icon: 'fa-heartbeat', route: 'settings', query: () => ({ tab: 'tab_veh_status_mgmt' }),
            desc: 'Status board, inspection, and maintenance options.',
            gate: ctx => ctx.isEffectiveAdmin || hasVsmAccess(ctx.features) },
          { key: 'bus_sos_settings', label: 'Bus SOS Settings', icon: 'fa-exclamation-triangle', route: 'settings', query: () => ({ tab: 'tab_bus_sos' }),
            desc: 'Emergency SOS recipients, range, and vehicle coverage.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.bus_sos_enabled) },
          // ERM settings live under the Event Response Manager section since
          // 2026-10-01 (its erm_settings row). staff configure a package before
          // it's turned on (6701bb53), and admin demo mode now shows them that
          // section on an account without ERM, row included -- so this second
          // way in is only for a yard-only account, whose rail strips the ERM
          // section (YARD_ONLY_RAIL). anywhere else it listed ERM settings
          // twice (FI-2263). staff-only, so it takes the purple cue by itself.
          { key: 'erm_settings', label: 'Event Response Manager', favLabel: 'ERM Settings', icon: 'fa-flag', route: 'settings', query: () => ({ tab: 'tab_erm' }),
            desc: 'Categories, recipients, the public form, and the huddle.',
            gate: ctx => ctx.isEffectiveAdmin && isYardOnly(ctx) },
          { key: 'data_services', label: 'Data Services', favLabel: 'Data Services Settings', icon: 'fa-refresh', route: 'settings', query: () => ({ tab: 'tab_data_services' }),
            desc: 'Data sync services for drivers and vehicles.',
            gate: ctx => ctx.isEffectiveAdmin || isFeatureEnabled(ctx.features.data_services_enabled) },
          // hardcoded anderson customer-admin grant (tenant_config_shell.vue:253-255)
          { key: 'fa_settings', label: 'Fleet Assistant', favLabel: 'Fleet Assistant Settings', icon: 'fa-magic', route: 'settings', query: () => ({ tab: 'tab_fleet_assistant' }),
            desc: 'Fleet Assistant access and behavior for this account.',
            gate: ctx => ctx.isEffectiveAdmin || (ctx.tenant === TENANTS.ANDERSON && ctx.role === 'customer-admin') },
          { key: 'history', label: 'Settings History', icon: 'fa-history', route: 'settings', query: () => ({ tab: 'tab_history' }),
            desc: 'A log of settings changes on this account.' },
          // last, below the configuration tabs: the one recurring operational task
          { key: 'manage_users', label: 'Manage Users', shortLabel: 'Users', icon: 'fa-users', route: 'settings', query: () => ({ tab: 'tab_user_list' }),
            desc: 'Add users, set roles, and manage access.' },
          // always available, no extra gate: the section gate above already limits Settings to
          // admin / customer-admin, and both see this tab -- staff can toggle, customer-admins get
          // it read-only (tenant_config_packages.vue's canEdit). It is the single place to see
          // every product package, since a disabled package's own settings tab is hidden.
          { key: 'packages', label: 'Packages & Billing', shortLabel: 'Packages', icon: 'fa-cubes', route: 'settings', query: () => ({ tab: 'tab_packages' }),
            desc: 'See which product packages this account has, and what they cost.' },
        ],
      },
    ],
  },

  {
    key: 'admin',
    label: 'Admin Tools',
    icon: 'fa-wrench',
    desc: 'Staff dashboards and system tools.',
    surfacing: 'utility',
    landingRoute: () => 'admin_tools_dashboard',
    gate: ctx => ctx.isEffectiveAdmin,
    groups: [
      {
        key: 'tools',
        items: [
          { key: 'summary', label: 'Summary Dashboard', icon: 'fa-tachometer', route: 'admin_dashboard',
            desc: 'High-level overview of system health, tenant activity, and key metrics.' },
          { key: 'tenants', label: 'Account & Billing Stats', icon: 'fa-usd', route: 'tenant_manager',
            desc: 'Tenant account details, subscription status, and billing statistics.' },
          { key: 'api_usage', label: 'API Usage', icon: 'fa-exchange', route: 'api_usage_dashboard',
            desc: 'API request volumes, response times, and usage breakdowns by tenant.' },
          { key: 'sms_usage', label: 'SMS Usage', icon: 'fa-comments-o', route: 'sms_usage_dashboard',
            desc: 'SMS send/receive volumes, delivery status, and carrier costs by tenant.' },
          { key: 'fleet_map', label: 'Fleet Map', icon: 'fa-map', route: 'admin_fleet_map',
            desc: 'Live vehicle positions across every tenant, with counts by tenant type and telematics provider.' },
          { key: 'processes', label: 'Process Manager', icon: 'fa-tasks', route: 'process_manager_list',
            desc: 'View and manage background processing jobs across all tenants.' },
          { key: 'simulator', label: 'Actuals Simulator', icon: 'fa-flask', route: 'actuals_simulator',
            desc: 'Simulate actuals processing to test and debug booking matching logic.' },
          { key: 'shortener', label: 'URL Shortener', icon: 'fa-link', route: 'url_shortener',
            desc: 'Create and manage short URLs for sharing fleet integration links.' },
          { key: 'cache', label: 'Cache Manager', icon: 'fa-database', route: 'cache_manager',
            desc: 'Inspect and flush cached data for tenants and system resources.' },
        ],
      },
      {
        // moved out of Help 2026-07-30 (Chaz): these three are staff-only
        // surfaces that happen to RENDER as help_center tabs, so they read as
        // Admin Tools rather than as help. the routes are unchanged -- each
        // still deep-links to its help_center tab. the classic navbar keeps
        // them under Help; this registry only drives the horizon rail.
        key: 'help_admin',
        label: 'Assistant & Feedback',
        icon: 'fa-shield',
        items: [
          { key: 'feedback_review', label: 'Feedback Review', icon: 'fa-comments-o', route: 'help_center',
            query: () => ({ tab: 'feedback' }),
            desc: 'Review feedback left on assistant answers.' },
          { key: 'assistant_insights', label: 'Assistant Insights', icon: 'fa-lightbulb-o', route: 'help_center',
            query: () => ({ tab: 'insights' }),
            desc: 'Fleet Assistant usage and cost across tenants.' },
          { key: 'help_announcements', label: 'Announcements', icon: 'fa-bullhorn', route: 'help_center',
            query: () => ({ tab: 'announcements' }),
            desc: 'Compose and publish product announcements.' },
        ],
      },
    ],
  },

  {
    // promoted out of Admin Tools 2026-07-30 (Chaz) to a main section of its own.
    // THE SECTION ITSELF IS OPEN TO EVERYONE and the staff-only rows carry their
    // own gate, rather than the section gating as a whole -- that is what lets
    // customers reach the vsm board preview while the other two stay internal.
    // resolveSection drops a section whose items all gate out (it has no
    // landingRoute), so a viewer who can see none of these never sees Preview.
    key: 'preview',
    label: 'Preview',
    // fa-eye, not fa-flask: the actuals simulator over in Admin Tools owns the
    // flask, and these are things to LOOK at rather than experiments
    icon: 'fa-eye',
    desc: 'Redesigned surfaces, still being trialled.',
    surfacing: 'utility',
    gate: () => false, // Preview removed from the left nav (2026-09-10); routes stay reachable by URL
    groups: [
      {
        key: 'preview',
        items: [
          // open to every ROLE that may see vsm at all, but only where the
          // tenant actually HAS vsm. the v1 row above can be laxer than this:
          // when the feature is off, vsmRouteName sends it to the /vehstatusmgr
          // upsell page instead. this row points at the real board, so with no
          // feature it would just land on "No vehicle data for this fleet".
          // its rail carries a Preview badge, which is what tells a customer
          // this sits alongside the classic board rather than replacing it.
          { key: 'vsm_board', label: 'Vehicle Status Manager v2', icon: 'fa-heartbeat', route: 'vsm_board',
            desc: 'Redesigned vehicle status board, in preview. The classic version stays under Vehicle Status Manager.',
            gate: ctx => vsmRoleGate(ctx) && hasVsmAccess(ctx.features),
            query: ctx => ({ tenant: ctx.tenant }) },
          // staff-only, gated per row now that the section is open. these two
          // used to inherit isEffectiveAdmin from Admin Tools.
          { key: 'today_board', label: 'Today Board', icon: 'fa-columns', route: 'ops_board',
            gate: ctx => ctx.isEffectiveAdmin,
            desc: 'Exception-first dispatch board for today\'s departures, active trips, and returns.' },
          // the erm left preview earlier: staff reach it from the top-level ERM section
          { key: 'departure_board_preview', label: 'Departure Board', icon: 'fa-sign-out', route: 'departure_board',
            gate: ctx => ctx.isEffectiveAdmin,
            desc: 'Redesigned departure board with display modes suited to wall screens.',
            query: ctx => ({ tenant: ctx.tenant }) },
        ],
      },
    ],
  },

  // contextual rows for the top-bar chrome destinations (see the surfacing
  // note above). the profile landing carries the viewer's own username so
  // an admin opening ANOTHER user from manage users does not light this up
  // (fi_section_nav falls back to the settings path match there). the help
  // center's contextual row retired in round 13: the help section above
  // lists it permanently now.
  {
    key: 'notifications',
    label: 'Notifications',
    icon: 'fa-bell-o',
    desc: 'Your notification history.',
    surfacing: 'contextual',
    landingRoute: () => 'notifications_list',
    landingOnly: true,
    gate: () => true,
    groups: [],
  },
  {
    key: 'profile',
    label: 'Manage Profile',
    icon: 'fa-user-o',
    desc: 'Your account details and preferences.',
    surfacing: 'contextual',
    landingRoute: () => 'user_detail',
    landingQuery: ctx => ({ username: (ctx.user && ctx.user.username) || '' }),
    landingOnly: true,
    gate: () => true,
    groups: [],
  },

]

// utility destinations that live in the top bar rather than the section tree.
// theme picker, view-as, profile, and logout are component behaviors, not nav
// destinations, so they are wired in FiTopNav directly.
export const UTILITY_ITEMS = [
  { key: 'notifications', label: 'Notifications', icon: 'fa-bell-o', route: 'notifications_list',
    placement: 'topbar', gate: () => true },
]

// the top-bar help icon (classic parity position, navbar.vue:17-22) opens a
// compact menu instead of a sidebar section: the help center's top-level tabs
// (help_center.vue:3061-3068) plus the public legal pages on the company
// site. the icon itself always renders -- terms/privacy must stay reachable
// even when a tenant's help center is disabled -- so the feature gate lives
// on the help-center group here, not on the icon.
export const HELP_MENU_GROUPS = [
  {
    key: 'help_center',
    // default-on explicit-false pattern (navbar.vue:18-22)
    gate: ctx => ctx.isEffectiveAdmin || ctx.features.help_center_enabled !== false,
    items: [
      { key: 'help_articles', label: 'Help Articles', icon: 'fa-book', route: 'help_center',
        query: () => ({ tab: 'articles' }) },
      // non-admins additionally need the company-docs flag (help_center.vue:2877-2881)
      { key: 'company_docs', label: 'Company Docs', icon: 'fa-file-text-o', route: 'help_center',
        query: () => ({ tab: 'company' }),
        gate: ctx => (ctx.isEffectiveAdmin || ctx.features.company_documents_in_help_center !== false) && !isYardOnly(ctx) },
      { key: 'support_tickets', label: 'Support Tickets', icon: 'fa-ticket', route: 'help_center',
        query: () => ({ tab: 'support' }) },
      { key: 'support_ticket_board', label: 'Ticket Board', favLabel: 'Tickets', icon: 'fa-columns', route: 'support_ticket_board',
        gate: ctx => ctx.isEffectiveAdmin },
    ],
  },
  {
    key: 'help_admin',
    label: 'Admin',
    gate: ctx => ctx.isEffectiveAdmin,
    items: [
      { key: 'feedback_review', label: 'Feedback Review', icon: 'fa-comments-o', route: 'help_center',
        query: () => ({ tab: 'feedback' }) },
      { key: 'assistant_insights', label: 'Assistant Insights', icon: 'fa-lightbulb-o', route: 'help_center',
        query: () => ({ tab: 'insights' }) },
      { key: 'help_announcements', label: 'Announcements', icon: 'fa-bullhorn', route: 'help_center',
        query: () => ({ tab: 'announcements' }) },
    ],
  },
  // kept in step with the help section's loose group in SECTIONS above:
  // one destination, one icon, whichever surface the user reaches it from.
  // its own group so the divider separates browsing the product from the
  // legal pages below.
  {
    key: 'help_explore',
    items: [
      { key: 'site_map', label: 'Site Map', icon: 'fa-sitemap', route: 'site_map',
        gate: ctx => !isLockedRole(ctx) && !isDriver(ctx) },
    ],
  },
  {
    key: 'legal',
    items: [
      { key: 'terms', label: 'Terms of Service', icon: 'fa-gavel', external: true,
        href: () => LEGAL_URLS.terms },
      { key: 'privacy', label: 'Privacy Policy', icon: 'fa-lock', external: true,
        href: () => LEGAL_URLS.privacy },
    ],
  },
]

// ---------------------------------------------------------------------------
// resolution
// ---------------------------------------------------------------------------

function resolveValue(v, ctx) {
  return typeof v === 'function' ? v(ctx) : v
}

// admin demo mode (FI-2178): an effective admin can see -- and open -- every
// feature-gated destination even on accounts where the flag is off, so support
// can demo a product before it is turned on for the customer. we do NOT hand
// admins a blanket "show everything": we re-run a failing gate against a ctx
// whose *_enabled feature flags are all forced on. structural gates (role,
// tenant type, telematics type, driver/booking-customer lockdowns, and
// account-MODE flags that don't end in _enabled such as bus_sos_yard_only)
// read their real values and still fail, so a CM admin never sees TBN-only
// rows and a yard-only account stays trimmed. because the bypass is additive
// (OR, never AND) it can only ADD visibility -- a "hide when the flag is on"
// gate can't be turned off by it. this generalizes the many hand-written
// `ctx.isEffectiveAdmin || isFeatureEnabled(...)` gates already in SECTIONS;
// with it, those or-arms are redundant but harmless, and the two deliberate
// no-staff-bypass sections (ERM, Coaching) now demo like everything else
// (ben, 2026-09-29 -- reverses the 2026-08-28 erm_access.js note). rows shown
// ONLY through this bypass are still marked staffOnly (wizard purple) via
// customerTwin, which keeps the real flags, so an admin can always tell what
// the customer actually has.
const ALL_FEATURES_ON = {
  get(target, prop, receiver) {
    if (typeof prop === 'string' && prop.endsWith('_enabled')) return true
    return Reflect.get(target, prop, receiver)
  },
}

function adminDemoCtx(ctx) {
  const features = new Proxy(ctx.features || {}, ALL_FEATURES_ON)
  // a few gates read the raw user object's per-tenant feature map instead of
  // ctx.features (hasGroupMessageAccess, hasVsmHistoryAccess). boost that copy
  // too so demo mode reaches them; customerTwin keeps the real user, so the
  // staff cue is unaffected.
  let user = ctx.user
  if (user && user.features && user.tenant) {
    user = {
      ...user,
      features: {
        ...user.features,
        [user.tenant]: new Proxy(user.features[user.tenant] || {}, ALL_FEATURES_ON),
      },
    }
  }
  return { ...ctx, features, user }
}

// a gate passes when it passes for the real viewer, or -- for an effective
// admin only -- when it passes with every feature flag forced on (demo mode).
// no gate (null/undefined) always passes.
function gatePasses(gate, ctx) {
  if (!gate) return true
  if (gate(ctx)) return true
  return !!ctx.isEffectiveAdmin && gate(adminDemoCtx(ctx))
}

// the viewer re-imagined as the highest-privilege customer. an element is
// staff-cued (wizard purple in the rail) when its gate passes for the real
// viewer but fails for this twin -- i.e. no customer on this tenant could
// see it. computed mechanically so an or-gate (staff OR feature flag, staff
// OR customer-admin) cues only where the staff arm is doing the work, and
// a tenant that turns the flag on stops cueing by itself.
function customerTwin(ctx) {
  return {
    ...ctx,
    isEffectiveAdmin: false,
    role: 'customer-admin',
    user: { ...ctx.user, role: 'customer-admin' },
  }
}

function staffOnlyGate(gate, ctx) {
  return !!(ctx.isEffectiveAdmin && gate && !gate(customerTwin(ctx)))
}

function resolveItem(item, ctx, inheritStaff) {
  if (!gatePasses(item.gate, ctx)) return null
  const resolved = {
    key: item.key,
    // a function label lets one item read differently to different audiences
    // (My Scorecard vs Individual Driver), like route/href/query already can
    label: resolveValue(item.label, ctx),
    // optional tighter name for width-constrained surfaces (the favorites
    // toolbar). resolved items are rebuilt field by field, so anything not
    // listed here never reaches navDestinations and its consumers.
    shortLabel: item.shortLabel || null,
    // optional favorites-surface name for items whose bare label is
    // ambiguous once favorited (the section children all called
    // 'Dashboard'): both favorites surfaces prefer it over label
    favLabel: item.favLabel || null,
    icon: item.icon || null,
    desc: item.desc || null,
    external: !!item.external,
    placement: item.placement || 'rail',
    staffOnly: !!inheritStaff || staffOnlyGate(item.gate, ctx),
    // a convenience link into a page another section owns: it still renders
    // in its tree, but navDestinations lists it after the owning rows so
    // first-match lookups (favorites, the star) land on the owner
    shortcut: !!item.shortcut,
  }
  if (item.action) {
    // no destination: the rail renders a button that emits this on $root
    resolved.action = item.action
  } else if (item.href) {
    resolved.href = resolveValue(item.href, ctx)
  } else {
    resolved.to = { name: resolveValue(item.route, ctx) }
    const query = resolveValue(item.query, ctx)
    if (query) resolved.to.query = query
    if (item.loose) resolved.loose = true
  }
  return resolved
}

function resolveGroup(group, ctx, inheritStaff) {
  if (!gatePasses(group.gate, ctx)) return null
  const staffOnly = !!inheritStaff || staffOnlyGate(group.gate, ctx)
  const items = group.items.map(i => resolveItem(i, ctx, staffOnly)).filter(Boolean)
  if (!items.length) return null
  return { key: group.key, label: group.label || null, icon: group.icon || null, staffOnly, items }
}

function resolveSection(section, ctx) {
  if (!gatePasses(section.gate, ctx)) return null
  const staffOnly = staffOnlyGate(section.gate, ctx)
  const groups = section.groups.map(g => resolveGroup(g, ctx, staffOnly)).filter(Boolean)
  let landing = section.landingRoute ? { name: resolveValue(section.landingRoute, ctx) } : null
  if (landing && section.landingQuery) landing.query = resolveValue(section.landingQuery, ctx)
  if (!landing) {
    const first = groups.length && groups[0].items.find(i => i.to && i.placement === 'rail')
    landing = first ? first.to : null
  }
  // a section with no landing and no content has nothing to show
  if (!landing && !groups.length) return null
  // sections without a fixed landing exist only through their items
  if (!section.landingRoute && !groups.some(g => g.items.some(i => i.placement === 'rail'))) return null
  return {
    key: section.key,
    label: section.label,
    // optional tighter name for width-constrained surfaces (the favorites
    // toolbar), for a section whose landing is itself favoritable
    shortLabel: section.shortLabel || null,
    favLabel: section.favLabel || null,
    icon: section.icon || null,
    desc: section.desc || null,
    surfacing: section.surfacing,
    landing,
    // true when landing is a real section home page (landingRoute) rather
    // than the first-rail-item fallback -- the tree renders an Overview row
    // only for real home pages so landing-less sections (alerts, vsm) don't
    // get a duplicate first item
    ownLanding: !!section.landingRoute,
    landingOnly: !!section.landingOnly,
    staffOnly,
    groups,
  }
}

// a yard-only Bus SOS account has no telematics and no other platform PRODUCT, so its rail is
// deliberately trimmed to Dashboard and Bus SOS -- the Bus SOS section carries the SOS Yard Map,
// which is the only map a yard-only account needs, so the general Fleet Map is dropped entirely
// (it has no vehicles to plot) -- plus the two utility sections every account needs: Help, and
// Settings (a yard-only customer-admin still has to manage users and configure Bus SOS
// recipients/range/yards). Settings is safe to admit whole: its tabs are individually feature-gated,
// so a yard-only account only ever sees General, Map Settings, Bus SOS Settings, Settings History
// and Manage Users -- the Bookings/VSM/Reports/Alerts/Packages/FA tabs self-hide because those
// packages are off. Keyed by top-level section: `true` keeps the whole section, an array keeps only
// those item keys within it.
const YARD_ONLY_RAIL = {
  dashboard: true,
  sos: true,   // key is 'sos' (registry SECTIONS), not 'bus_sos'; carries the SOS Yard Map (sos_map)
  help: true,
  settings: true,
}

export function isYardOnly(ctx) {
  return isFeatureEnabled(ctx.features.bus_sos_yard_only)
}

// a "package" is a licensable product module. Its top-level nav section (and its Settings tab) are
// hidden when the tenant does not have that package enabled -- for everyone except an effective admin,
// who keeps it (cued staff-only) under admin demo mode, see resolveNav / gatePasses. Keyed by
// the section `key` -> a predicate true when the package IS enabled. Sections not listed here are
// core / always-available (Dashboard, Maps, Bookings, Settings, Admin Tools, Help). `dispatch` is
// intentionally omitted: it is a mixed section (smart-dispatch group + reports-fed boards) that
// already self-gates per group and drops when empty, so a blanket section gate would over-hide it.
export const PACKAGE_GATES = {
  alerts: ctx => isFeatureEnabled(ctx.features.alerts_enabled),
  // FASTTRAK bundles VSM (vsmBoardRouteName hardcodes it with no feature check), so it is always
  // "enabled" there; every other tenant type needs one of the VSM flags
  vsm: ctx => hasVsmAccess(ctx.features) || ctx.tenantType === 'FASTTRAK',
  reports: ctx => isFeatureEnabled(ctx.features.views_reports_enabled) ||
                  isFeatureEnabled(ctx.features.actuals_enabled) ||
                  isFeatureEnabled(ctx.features.views_reports_otp_report_enabled),
  driver_manager: ctx => isFeatureEnabled(ctx.features.driver_scorecard_enabled),
  coaching_manager: ctx => isFeatureEnabled(ctx.features.coaching_portal_enabled),
  // sync removed 2026-09-29 (deprecated; see the removed Sync section above)
  sos: ctx => isFeatureEnabled(ctx.features.bus_sos_enabled),
  erm: ctx => isFeatureEnabled(ctx.features.ops_desk_enabled),
}

// the full visible nav tree for a user: gates applied, dynamic routes and
// queries resolved. plain serializable data; components just render it.
export function resolveNav(ctx) {
  let sections = SECTIONS.map(s => resolveSection(s, ctx)).filter(Boolean)
  // hide any product package the tenant hasn't enabled. an effective admin is
  // the exception (admin demo mode): the section is kept so support can demo
  // the product before it's bought, and cued staff-only (wizard purple) since
  // no customer on this account could see it. everyone else: hidden entirely.
  sections = sections.filter(s => {
    const pkg = PACKAGE_GATES[s.key]
    if (!pkg || pkg(ctx)) return true
    if (ctx.isEffectiveAdmin) { s.staffOnly = true; return true }
    return false
  })
  if (!isYardOnly(ctx)) return sections
  // strip everything a yard-only account shouldn't see; keep Maps' Fleet Map item only
  return sections
    .filter(s => YARD_ONLY_RAIL[s.key])
    .map(s => {
      const allow = YARD_ONLY_RAIL[s.key]
      if (allow === true) return s
      const groups = (s.groups || [])
        .map(g => ({ ...g, items: (g.items || []).filter(it => allow.includes(it.key)) }))
        .filter(g => g.items.length)
      return { ...s, groups }
    })
}

export function resolveUtilities(ctx) {
  return UTILITY_ITEMS.map(i => resolveItem(i, ctx)).filter(Boolean)
}

// the gated help menu for the top-bar help icon: [{ key, label?, items }]
export function resolveHelpMenu(ctx) {
  return HELP_MENU_GROUPS.map(g => resolveGroup(g, ctx)).filter(Boolean)
}

// items flagged for the top bar (sos, notifications) regardless of section.
// walks the raw data, not resolveNav: a section dropped from the rail for
// having no rail items (e.g. sos, whose only item is the topbar alarm) must
// still surface its topbar items.
export function resolveTopBarItems(ctx) {
  const out = []
  for (const section of SECTIONS) {
    if (section.gate && !section.gate(ctx)) continue
    for (const group of section.groups) {
      if (group.gate && !group.gate(ctx)) continue
      for (const item of group.items) {
        if (item.placement !== 'topbar') continue
        const resolved = resolveItem(item, ctx)
        if (resolved) out.push(resolved)
      }
    }
  }
  return out.concat(resolveUtilities(ctx).filter(i => i.placement === 'topbar'))
}

// gate for the fleet assistant launcher (floating pill in App.vue, bar
// button in fi_top_nav): off on the full-page FA route, honors the tenant
// feature flag (explicit false only -- missing means enabled), the role
// allowlist / per-user access grant, and the hide-launcher preference.
// takes the raw user (not ctx): App.vue calls this before buildNavContext.
export function faLauncherAvailable(user, routeName) {
  if (routeName === 'fleet_assistant') return false
  if (!user || !user.features || !user.features[user.tenant]) return false
  if (user.features[user.tenant].fleet_assistant_enabled === false) return false
  // yard-only Bus SOS accounts have no fleet data to ask about, so never offer the assistant
  if (isFeatureEnabled(user.features[user.tenant].bus_sos_yard_only)) return false
  const roleOk = user.role === 'admin' || user.role === 'customer' ||
    user.role === 'customer-admin' ||
    user.role === 'manager' || user.role === 'dispatch' || user.fleet_assistant_access
  if (!roleOk) return false
  return !(user.preferences && user.preferences.hide_fleet_assistant_launcher)
}

// ---------------------------------------------------------------------------
// destinations (favorites)
// ---------------------------------------------------------------------------
// query keys that DISAMBIGUATE two rows sharing one route. a row that does not
// pin them must not claim a url that carries them (else "Help Articles" would
// also answer for ?tab=insights). mirrors fi_section_nav's isItemActive.
const DISAMBIGUATORS = ['source', 'section', 'tab']

// every favoritable destination in a resolved nav, flattened. section landings
// are included only when the section owns a real landing page: a synthesized
// landing is just its own first item wearing the section's name, and would
// collide with that item on the same key.
export function navDestinations(nav) {
  const out = []
  // shortcut rows (see resolveItem) go after every owned destination, in
  // their own nav order, so the first match for a shared route is the owner
  const shortcuts = []
  for (const section of nav) {
    if (section.landing && section.ownLanding) {
      out.push({
        key: 'section:' + section.key,
        label: section.label,
        shortLabel: section.shortLabel,
        favLabel: section.favLabel,
        icon: section.icon,
        desc: section.desc,
        to: section.landing,
        sectionKey: section.key,
        sectionLabel: section.label,
      })
    }
    for (const group of section.groups) {
      for (const item of group.items) {
        if (item.placement !== 'rail' || !item.to) continue
        const bucket = item.shortcut ? shortcuts : out
        bucket.push({
          key: section.key + ':' + group.key + ':' + item.key,
          label: item.label,
          // optional tighter name for width-constrained surfaces (the
          // favorites toolbar); the rail and site map keep item.label
          shortLabel: item.shortLabel,
          // disambiguating favorites-surface name (see resolveItem)
          favLabel: item.favLabel,
          icon: item.icon || section.icon,
          desc: item.desc,
          to: item.to,
          loose: !!item.loose,
          sectionKey: section.key,
          sectionLabel: section.label,
        })
      }
    }
  }
  return out.concat(shortcuts)
}

// how well a destination answers for the route the user is actually on.
// returns -1 for "not this one", else the count of pinned query keys it
// matched (higher = more specific, so ?tab=insights beats the bare page).
function destinationScore(dest, route) {
  if (!route || !dest.to || dest.to.name !== route.name) return -1
  const pinned = dest.to.query || {}
  const actual = route.query || {}
  let score = 0
  for (const k of Object.keys(pinned)) {
    if (actual[k] == null) continue // url omits it: still ours, just less specific
    if (String(actual[k]) !== String(pinned[k])) return -1
    score += 1
  }
  // a row that owns its whole multi-tab page skips the disambiguator guard
  if (dest.loose) return score
  for (const k of DISAMBIGUATORS) {
    if (actual[k] != null && pinned[k] == null) return -1
  }
  return score
}

// which nav destination IS the page at this route, or null when the route
// never appears in the nav (detail pages, deep links). the star button uses
// this so it favorites the canonical destination rather than the current url
// with whatever filters happen to be applied.
export function matchNavDestination(nav, route) {
  let best = null
  let bestScore = -1
  for (const dest of navDestinations(nav)) {
    const score = destinationScore(dest, route)
    if (score > bestScore) {
      best = dest
      bestScore = score
    }
  }
  return best
}

// the user's starred destinations, in their saved order, filtered to what
// they can still see. gates were already applied building `nav`, so a
// favorite for a page the user has lost access to just stops rendering.
// `keys` are the opaque canonical keys from nav_favorites.favKey.
//
// `extra` is an optional SECOND source of destinations, consulted only for
// keys the registry has none for -- the site map offers a star on pages the
// rail never lists (site_map_pages.siteMapFavoriteDestinations), and without
// it those stars save and then render nowhere. the registry wins every
// collision, so a page in both keeps its rail label, icon and description.
export function resolveFavorites(nav, keys, keyFor, extra) {
  if (!keys || !keys.length) return []
  const byKey = new Map()
  for (const dest of navDestinations(nav)) {
    const k = keyFor(dest.to)
    if (k && !byKey.has(k)) byKey.set(k, dest)
  }
  for (const dest of extra || []) {
    const k = dest && keyFor(dest.to)
    if (k && !byKey.has(k)) byKey.set(k, dest)
  }
  const out = []
  const seen = new Set()
  for (const key of keys) {
    if (seen.has(key)) continue
    seen.add(key)
    const hit = byKey.get(key)
    if (hit) out.push(hit)
  }
  return out
}

// active-section lookup for the top nav highlight: route name -> section key.
// covers landings and rail items; callers fall back to path-prefix matching
// for deep/detail routes that never appear in the nav.
export function sectionKeyForRoute(ctx, routeName) {
  if (!routeName) return null
  const nav = resolveNav(ctx)
  // a section whose own landing page IS the route owns it, ahead of any
  // earlier section that merely links into it (the alerts shortcut into
  // settings would otherwise claim every settings tab)
  for (const section of nav) {
    if (section.ownLanding && section.landing && section.landing.name === routeName) return section.key
  }
  for (const section of nav) {
    if (section.landing && section.landing.name === routeName) return section.key
    for (const group of section.groups) {
      if (group.items.some(i => i.to && i.to.name === routeName)) return section.key
    }
  }
  return null
}
